import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { QueryClient, QueryClientProvider, useQueryClient, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FiSliders } from 'react-icons/fi';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { useWatchlistData, fetchScreenData, useEconomicData, useMarketAnalysis, updateWatchlist, updateUserData, useCategories, createUserList,useUserLists, fetchCustomFilterResults, addFavoriteLists, useWatchlistLabels, useCallHighlights, useMoverNews, useSignalWatches, useListPerformance, useAssetBuckets, useGuidanceSeason } from '../services/api';
import { isStrategyTab, findStrategyForTab, strategySymbols, strategyCaption } from '../utils/strategyTabs';
import { useMarketHoursRefresh } from '../utils/marketRefresh';
import LabelManagerModal from './LabelManagerModal';
import PurchaseInfoModal from './PurchaseInfoModal';
import OnboardingWizard from './OnboardingWizard';
import StockTable from './StockTable';
import CONFIG from '../config/config';
import axios from 'axios';
import './App.css';
import Header from './Header';
import Footer from './Footer';
import EarningsWidget from './EarningsWidget';
//import DailyMovementsWidget from './DailyMovementsWidget';
import AddSymbolButton from './AddSymbolButton';
import Modal from 'react-modal';
import ProfileModal from './ProfileModal';
import NotificationsModal from './NotificationsModal';
import { DEFAULT_COLOR_SETTINGS } from '../config/constants';
//import MarketAnalysisRow from './MarketAnalysis';
import MarketAnalysisRow from './MarketAnalysisRow2';
// import ThesesPanel from './ThesesPanel'; // replaced by EarningsSchedulePanel to save vertical space — may bring back later
import MobileBadgeTape from './MobileBadgeTape';
import MobileFeedSettings from './MobileFeedSettings';
import {
  MOBILE_FEED_SECTIONS,
  isFeedSectionEnabled,
  countEnabledSections,
} from '../config/mobileFeedSections';
import StrategiesWidget from './StrategiesWidget';
import MobileEarningsView from './earnings/MobileEarningsView';
import EarningsSchedulePanel from './earnings/EarningsSchedulePanel';
import { daysUntilEarnings } from './utils';
import { explanationFor, fmtMovePct } from '../utils/moverNews';
import { rankListMovers, breadthText, formatListMove } from '../utils/listMovers';
import { buildCrowdedBucketCards } from '../utils/crowdedBucketCards';
import { buildGuidanceSeasonCard } from '../utils/guidanceSeasonCard';
import { sentimentVisual, splitBarVisual, rangeBarVisual } from '../utils/briefVisuals';
import {
  buildWatchlistDigest, formatWatchlistDigest, rankByMove, reportsWithinHorizon, todaysMoveOnly, topMovers,
} from '../utils/watchlistDigest';
import { DEFAULT_COLUMNS, ALL_COLUMNS } from '../config/columnConfig'; // Added import
import { loadSettings, saveSettings } from '../utils/settings';
import { migrateIndustriesToSectors } from '../utils/sectorsFilterMigration';
import { renderMarketText } from '../utils/marketText';
import { formatMarketOverviewUpdated } from '../utils/marketOverviewFreshness';
import {
  describeYieldCurve,
  describeBreadth,
  describeIndustryShare,
  formatIndustryShare,
} from '../utils/briefInterpretations';
import FilterDropdowns from './FilterDropdown';
import SettingsDialog from './SettingsDialog';
import { MARKET_CAP_TIERS } from './MarketCapFilter';
import { SECURITY_TYPES } from './TypeFilter';
import ReactDOM from 'react-dom';
import { publicFetch } from '../utils/authenticatedFetch';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './landing/LandingPage';
import StockPage from './StockPage'; // NEW IMPORT
import SyncConflictDialog, { SYNC_RESOLUTION } from './SyncConflictDialog';
import ListRouteSync, { HOMEPAGE_DEFAULT_TAB } from './ListRouteSync';
import ListSEO from './ListSEO';
import PortfolioView from './PortfolioView';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LoginProvider } from '../contexts/LoginContext';
import journeyTracker from '../utils/journeyTracker';
import EarningsPage from './earnings/EarningsPage';
import ListsIndexPage from './ListsIndexPage';
import { getListPath, isShareableListId, findSmartList } from '../utils/listUrl';
import ConsentBanner from './legal/ConsentBanner';
import { TermsPage, PrivacyPage, CookiePolicyPage } from './legal/LegalPages';
import McpPage from './mcp/McpPage';
import McpConnectPanel from './mcp/McpConnectPanel';

import { GOOGLE_CLIENT_ID } from '../config/config';

library.add(fas);

// Storage key for sync preference
const SYNC_PREFERENCE_KEY = 'syncResolutionPreference';

const DEBUG = {
  columns: false,    // Column-related logs
  data: true,       // Data loading logs
  filters: true,    // Filter-related logs
  sync: false,      // User data sync logs
  settings: false,  // Settings-related logs
  market: false     // Market analysis logs
};

// Discount status bar disabled — will be re-implemented with new tier system
// const APP_STATUS = {
//   message: "🚀 This app is still in active development mode. 💎 Use code VfjF0RPi for 50% OFF FOREVER discount as a thank you for your early support! ⭐",
//   type: "info"
// };
const APP_STATUS = null;

const MOBILE_BREAKPOINT = 768;

// How far back a company can have reported for its call takeaway to still be feed
// material, and how many of those tickers to ask the highlights endpoint about.
const CALL_HIGHLIGHT_LOOKBACK_DAYS = 14;
const MAX_CALL_HIGHLIGHT_TICKERS = 25;
// Movers shown per direction. Enough to read as a ranking rather than a single
// anecdote, small enough that the round-robin interleave doesn't drown the feed.
const MOVERS_PER_SIDE = 3;

// Frozen so "no saved config" has ONE identity. A fresh `{}` per render would change
// the feed memo's dependency on every render and defeat the memo entirely.
const EMPTY_FEED_SECTIONS = Object.freeze({});

/**
 * `listExplanation` is a STRING for most tabs but an OBJECT for label tabs
 * (`{name, category, description, toString}`), so anything rendering it has to
 * flatten it first — `truncateText` calls `.replace`, which throws on an object
 * and takes the whole feed memo down with it.
 */
const explanationToText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') {
    const text = value.toString();
    return text === '[object Object]' ? (value.name || '') : text;
  }
  return '';
};

/**
 * A display name for the tab currently on screen.
 *
 * The plain lookup below only searches saved lists, categories and filters, so
 * every synthetic tab (the watchlist, label tabs, the ad-hoc symbol sets) missed
 * it and fell through to a placeholder — the Brief's "Active List" card read
 * "Current Focus" and its movers read "Your List". One resolver so the card and
 * the mover labels can never disagree about what the user is looking at.
 */
const resolveListDisplayName = ({ selectedTab, userListsData, categories, labelsData, strategies }) => {
  const listMatch = Array.isArray(userListsData)
    ? userListsData.find((list) => list.id === selectedTab)
    : undefined;
  const categoryMatch = Array.isArray(categories)
    ? categories.find((category) => category.id === selectedTab)
    : undefined;
  const filterMatch = findSmartList(categories, selectedTab);

  const match = listMatch || categoryMatch || filterMatch;
  const matchName = match?.name || match?.title || match?.label;
  if (matchName) return matchName;

  if (selectedTab === 'watchlist') return 'My Watchlist';
  if (selectedTab === 'url_symbols') return 'Page Mentions';

  if (selectedTab?.startsWith('label_')) {
    const labelId = selectedTab.slice('label_'.length);
    const label = labelsData?.labels?.find((entry) => entry.id === labelId);
    if (label?.name) return label.name;
  }

  if (selectedTab?.startsWith('earnings_day_')) {
    return `Earnings ${selectedTab.slice('earnings_day_'.length)}`;
  }

  if (selectedTab?.startsWith('symbols_')) return 'Selected Symbols';

  if (isStrategyTab(selectedTab)) {
    const strategy = findStrategyForTab(strategies, selectedTab);
    if (strategy?.name) return strategy.name;
  }

  return '';
};

const truncateText = (text, maxLength = 90) => {
  if (!text) return '';
  const normalized = String(text).replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 3)}...`;
};

const formatEventDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatRelativeTimeFromNow = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
};

const getEventMeta = (event) => {
  if (!event) return '';
  if (event.actual && event.actual !== 'N/A') {
    return `Actual ${event.actual}`;
  }
  if (event.forecast && event.forecast !== 'N/A') {
    return `Forecast ${event.forecast}`;
  }
  if (event.previous && event.previous !== 'N/A') {
    return `Prev ${event.previous}`;
  }
  return '';
};

const getEconomicSentiment = (analysis) => {
  if (!analysis) return '';
  if (analysis.sentiment_label) {
    return analysis.sentiment_label;
  }
  if (analysis.sentiment_score !== undefined && analysis.sentiment_score !== null) {
    return `Sentiment ${Math.round(analysis.sentiment_score * 100)}%`;
  }
  return '';
};

const cleanTicker = (ticker) => {
  if (!ticker) return '';
  return ticker.replace(/[$#]/g, '').toUpperCase();
};

const getTopSignal = (signals = []) => {
  if (!Array.isArray(signals) || !signals.length) return null;
  return [...signals]
    .map((signal) => ({
      ...signal,
      strength: parseFloat(signal.strength ?? 0),
    }))
    .filter((signal) => !Number.isNaN(signal.strength))
    .sort((a, b) => b.strength - a.strength)[0];
};

const formatPercent = (value, { withSign = true, fractionDigits = 1 } = {}) => {
  if (value === undefined || value === null) {
    return '';
  }
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numeric)) {
    return '';
  }
  const formatted = numeric.toFixed(fractionDigits);
  if (!withSign) {
    return `${formatted}%`;
  }
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${formatted}%`;
};

const parseISODate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const isDateInRange = (value, { minOffsetDays = 0, maxOffsetDays = 7 } = {}) => {
  const target = parseISODate(value);
  if (!target) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + minOffsetDays);

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxOffsetDays);

  return target >= minDate && target <= maxDate;
};

const getCompanyChangePercent = (company) => {
  if (!company) return null;
  // Top gainers/losers are a TODAY question, so only today's move qualifies.
  // The old fallbacks answered different questions entirely — stockChange is
  // the move since the last earnings report and fiftyTwoWeekChangePercent is a
  // year of it — so a stock with no live quote could be ranked "top gainer" on
  // a rally that happened months ago (company.percentChange was never mapped
  // by api.js at all, so that one had only ever been undefined). Better the
  // stock drops out of the ranking than enters it on the wrong number.
  const candidates = [company.regularMarketChangePercent];
  for (const value of candidates) {
    if (value === undefined || value === null) continue;
    const numeric = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }
  return null;
};

const formatPhaseTitle = (phase) => {
  if (!phase) return '';
  return phase
    .toString()
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getFearGreedDescription = (rating) => {
  const descriptions = {
    'extreme fear': 'Extremely pessimistic sentiment, historically a contrarian buy signal.',
    fear: 'Elevated pessimism and risk aversion.',
    neutral: 'Balanced sentiment without a clear bias.',
    greed: 'Growing optimism and risk appetite.',
    'extreme greed': 'Euphoric sentiment, often a contrarian sell signal.',
  };
  return descriptions[rating?.toLowerCase()] || '';
};

const formatSignedNumber = (value, fractionDigits = 2) => {
  if (value === undefined || value === null) {
    return '';
  }
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numeric)) {
    return '';
  }
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toFixed(fractionDigits)}`;
};

const parseMaturity = (maturity) => {
  if (!maturity) return 0;
  const unit = maturity.slice(-1).toUpperCase();
  const value = parseInt(maturity.slice(0, -1), 10) || 0;
  return unit === 'Y' ? value * 12 : value;
};

const computePearsonCorrelation = (xValues, yValues) => {
  if (!Array.isArray(xValues) || !Array.isArray(yValues)) return null;
  if (xValues.length !== yValues.length || xValues.length === 0) return null;

  const n = xValues.length;
  const meanX = xValues.reduce((sum, val) => sum + val, 0) / n;
  const meanY = yValues.reduce((sum, val) => sum + val, 0) / n;

  const numerator = xValues.reduce((accumulator, x, index) => {
    return accumulator + (x - meanX) * (yValues[index] - meanY);
  }, 0);

  const denomX = Math.sqrt(xValues.reduce((accumulator, x) => accumulator + (x - meanX) ** 2, 0));
  const denomY = Math.sqrt(yValues.reduce((accumulator, y) => accumulator + (y - meanY) ** 2, 0));
  const denominator = denomX * denomY;

  if (denominator === 0) {
    return null;
  }

  return numerator / denominator;
};

const buildBondHighlights = (curvesData) => {
  if (!curvesData) return null;

  const { yield_curve: yieldCurve = {}, zero_curve: zeroCurve = {}, discount_curve: discountCurve = {}, interpretation } = curvesData;
  const uniqueMaturities = new Set([
    ...Object.keys(yieldCurve),
    ...Object.keys(zeroCurve),
    ...Object.keys(discountCurve),
  ]);

  if (uniqueMaturities.size === 0) {
    return { interpretation: interpretation || null };
  }

  const chartData = Array.from(uniqueMaturities)
    .sort((a, b) => parseMaturity(a) - parseMaturity(b))
    .map((maturity) => ({
      maturity,
      yield: typeof yieldCurve[maturity] === 'number' ? yieldCurve[maturity] : null,
      zero: typeof zeroCurve[maturity] === 'number' ? zeroCurve[maturity] : null,
      discount: typeof discountCurve[maturity] === 'number' ? discountCurve[maturity] : null,
    }));

  const shortTerm = chartData.find((entry) => entry.maturity === '3M');
  const longTerm = chartData.find((entry) => entry.maturity === '10Y');

  const slope =
    shortTerm && longTerm && typeof shortTerm.yield === 'number' && typeof longTerm.yield === 'number'
      ? longTerm.yield - shortTerm.yield
      : null;

  // Shape label AND its plain-language reading come from one ladder in
  // briefInterpretations, so the card can never call the curve flat while the
  // sentence under it calls it steep.
  const curve = describeYieldCurve(slope);

  const paired = chartData.filter(
    (entry) => typeof entry.yield === 'number' && typeof entry.zero === 'number'
  );

  let correlation = null;
  let correlationSummary = '';
  if (paired.length >= 2) {
    const yieldValues = paired.map((entry) => entry.yield);
    const zeroValues = paired.map((entry) => entry.zero);
    correlation = computePearsonCorrelation(yieldValues, zeroValues);
    if (correlation !== null) {
      if (correlation > 0.85) {
        correlationSummary = 'Yield and zero curves are closely aligned across maturities.';
      } else if (correlation > 0.4) {
        correlationSummary = 'Yield and zero curves show partial alignment with some divergence.';
      } else {
        correlationSummary = 'Yield and zero curves diverge, hinting at segment-specific pricing.';
      }
    }
  }

  return {
    interpretation: interpretation || null,
    slope,
    slopeSummary: curve?.summary || '',
    slopeMeaning: curve?.meaning || '',
    correlation,
    correlationSummary,
  };
};

const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

const getIconName = (iconString) => {
  return iconString.startsWith('fa-') ? iconString.slice(3) : iconString;
};

const calculateTrialDaysLeft = (timestamp) => {
  const today = new Date();
  const firstUseDate = new Date(timestamp);
  const diffTime = Math.abs(today - firstUseDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, 21 - diffDays);
};

const filterCompaniesByMarketCap = (companies, selectedTiers) => {
  if (!Array.isArray(companies)) return [];
  if (!selectedTiers || selectedTiers.length === 0) return companies;
  
  if (DEBUG.filters) {
    console.log('Applying market cap tiers:', selectedTiers);
  }
  return companies.filter(company => {
    // If no market cap tiers are selected, show all companies
    if (selectedTiers.length === 0) return true;

    const marketCap = company.marketCap;

    // Include companies with null/undefined market cap if 'unknown' tier is selected
    if (marketCap === null || marketCap === undefined) {
      return selectedTiers.includes('unknown');
    }

    // Check if company's market cap falls within any of the selected tiers
    return selectedTiers.some(tierId => {
      const tier = MARKET_CAP_TIERS.find(t => t.id === tierId);
      if (!tier) return false;
      return marketCap >= tier.min && marketCap < tier.max;
    });
  });
};

// 1. Add a more robust loading control system
const loadingControl = {
  current: false,
  lastLoad: 0,
  minInterval: 1000, // Minimum time between loads in ms
  isThrottled: function() {
    const now = Date.now();
    return this.current || (now - this.lastLoad < this.minInterval);
  },
  start: function() {
    if (this.isThrottled()) return false;
    this.current = true;
    this.lastLoad = Date.now();
    return true;
  },
  end: function() {
    this.current = false;
  }
};

// Define a helper to poll for data on new symbols
async function pollForNewSymbols(symbols, fetchFn, maxWaitMs = 20000, intervalMs = 2000) {
  if (!Array.isArray(symbols)) {
    console.error('Invalid symbols passed to pollForNewSymbols:', symbols);
    return [];
  }

  return new Promise((resolve, reject) => {
    let elapsed = 0;

    const checkData = async () => {
      try {
        console.log('Polling for symbols:', symbols); // Debug log
        const data = await fetchFn(null, null, symbols);
        console.log('Poll response:', data); // Debug log

        if (Array.isArray(data) && data.length > 0) {
          resolve(data);
        } else {
          elapsed += intervalMs;
          if (elapsed >= maxWaitMs) {
            reject(new Error("Timeout waiting for data on new AI list."));
          } else {
            setTimeout(checkData, intervalMs);
          }
        }
      } catch (error) {
        console.error('Poll iteration error:', error); // Debug log
        reject(error);
      }
    };

    checkData();
  });
}

const App = ({ env }) => {
  // 1. User authentication state
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('info');
  
  // 2. User settings and lists state
  const [userSettings, setUserSettings] = useState(() => {
    const savedSettings = loadSettings();
    // One-time move from the old industries filter to sectors
    const migratedSettings = migrateIndustriesToSectors(savedSettings);
    if (migratedSettings !== savedSettings) {
      saveSettings('sectorsFilters', migratedSettings.sectorsFilters);
    }
    return migratedSettings || {
      colorSettings: DEFAULT_COLOR_SETTINGS,
      columns: DEFAULT_COLUMNS,
      marketCapFilters: MARKET_CAP_TIERS.map(tier => tier.id),
      typeFilters: SECURITY_TYPES.map(type => type.id),
      lists: [], // Initialize empty lists array
      sectorsFilters: [] // Initialize with empty array if not in settings
    };
  });

  // Add tableData and selectedSymbols state
  const [tableData, setTableData] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([]);

  // Not interested companies state
  const [notInterestedSymbols, setNotInterestedSymbols] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('notInterestedSymbols') || '[]');
    } catch (error) {
      console.error('Error loading not interested symbols:', error);
      return [];
    }
  });

  // 3. Modal state
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', content: null, size: 'md' });
  const [isColorSettingsOpen, setIsColorSettingsOpen] = useState(false);
  const [colorSettings, setColorSettings] = useState(DEFAULT_COLOR_SETTINGS);

  // Sync conflict dialog state
  const [syncConflictData, setSyncConflictData] = useState(null);
  const syncResolveRef = useRef(null); // Holds the promise resolver for sync dialog

  // Onboarding wizard state
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);

  // 4. Filter state
  const [activeFilters, setActiveFilters] = useState(() => ({
    types: userSettings.typeFilters || SECURITY_TYPES.map(type => type.id),
    marketCap: userSettings.marketCapFilters || MARKET_CAP_TIERS.map(tier => tier.id),
    sectors: userSettings.sectorsFilters || [] // Initialize with empty array if not in settings
  }));

  // 3. Selected tab state (depends on loggedInUser and URL)
  // Parse URL symbols from ?symbols= query param (for extension integration)
  const [urlSymbols, setUrlSymbols] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const symbolsParam = params.get('symbols');
    if (symbolsParam) {
      const symbols = symbolsParam.split(',').map(s => s.toUpperCase().trim()).filter(Boolean);
      console.log('[URL-SYMBOLS] Found symbols in URL:', symbols);
      return symbols;
    }
    return null;
  });

  // Parse ?expand=SYMBOL param to auto-expand a row (for extension integration)
  const [initialExpandSymbol] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const expandParam = params.get('expand');
    if (expandParam) {
      console.log('[URL-EXPAND] Found expand symbol in URL:', expandParam.toUpperCase());
      return expandParam.toUpperCase();
    }
    return null;
  });

  // Parse ?addToWatchlist=SYMBOL param to auto-add a symbol (for extension integration)
  const [addToWatchlistSymbol, setAddToWatchlistSymbol] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const addParam = params.get('addToWatchlist');
    if (addParam) {
      console.log('[URL-ADD-WATCHLIST] Found addToWatchlist symbol in URL:', addParam.toUpperCase());
      return addParam.toUpperCase();
    }
    return null;
  });

  const [selectedTab, setSelectedTab] = useState(() => {
    // Check URL for ?symbols= query param first (extension integration)
    const params = new URLSearchParams(window.location.search);
    if (params.get('symbols')) {
      console.log('[URL-SYMBOLS] Initial tab: url_symbols');
      return 'url_symbols';
    }

    // Check URL for /portfolio/:portfolioId
    const portfolioMatch = window.location.pathname.match(/^\/portfolio\/(.+)$/);
    if (portfolioMatch) {
      console.log('[LIST-ROUTE] Initial tab from URL (portfolio):', portfolioMatch[1]);
      return portfolioMatch[1];
    }

    // Check URL for /list/:listId
    const urlMatch = window.location.pathname.match(/^\/list\/(.+)$/);
    if (urlMatch) {
      console.log('[LIST-ROUTE] Initial tab from URL:', urlMatch[1]);
      return urlMatch[1];
    }

    if (loggedInUser) {
      return 'watchlist';
    }

    console.log('Initializing selectedTab...');
    const savedSymbols = localStorage.getItem('companiesSymbols');
    const watchlistSymbols = savedSymbols ? JSON.parse(savedSymbols) : [];
    console.log('Initial watchlist symbols:', watchlistSymbols);

    if (!watchlistSymbols.length) {
      return HOMEPAGE_DEFAULT_TAB;
    }
    return 'watchlist';
  });

  // 3. Rest of your existing state declarations
  const [companiesData, setCompaniesData] = useState([]);
  const [companiesSymbols, setCompaniesSymbols] = useState(() => {
    const savedSymbols = localStorage.getItem('companiesSymbols');
    return savedSymbols ? JSON.parse(savedSymbols) : [];
  });
  // Request to expand + scroll to one stock's row in the table (Signal Watches
  // widget). { symbol, nonce } — the nonce re-fires the same symbol on a repeat
  // click; StockTable resolves it once the symbol is in the loaded list.
  const [focusSymbol, setFocusSymbol] = useState(null);
  const focusNonceRef = useRef(0);
  // Ref to track symbols currently being added (prevents race condition duplicates)
  const addingSymbolsRef = useRef(new Set());
  // Ref to track if we're in the middle of a batch add operation (prevents sync from overwriting)
  const isBatchAddingRef = useRef(false);
  const [news, setNews] = useState([]);
  const [analysis, setAnalysis] = useState('');
  const [guidance, setGuidance] = useState('');
  const [relatedEquities, setRelatedEquities] = useState([]);
  const [error, setError] = useState('');
  const [firstUseTimestamp, setFirstUseTimestamp] = useState(() => {
    const savedTimestamp = localStorage.getItem('firstUseTimestamp');
    return savedTimestamp || new Date().toISOString();
  });
  const [trialDaysLeft, setTrialDaysLeft] = useState(() => calculateTrialDaysLeft(firstUseTimestamp));
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [selectedTimeframes, setSelectedTimeframes] = useState([0]);
  const [geography, setGeography] = useState('US');
  const [showEconomicEvents, setShowEconomicEvents] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileRefreshing, setIsMobileRefreshing] = useState(false);
  const [mobileTab, setMobileTab] = useState('brief'); // 'brief' | 'earnings' | 'signals'
  const [mobileBatchIndex, setMobileBatchIndex] = useState(1);
  const [isMobileLoadingMore, setIsMobileLoadingMore] = useState(false);
  const [showMobileFeedSettings, setShowMobileFeedSettings] = useState(false);
  const MOBILE_BATCH_SIZE = 20;

  const isMobile = useIsMobile();

  const isSyncing = useRef(false);
  const lastSyncedData = useRef({});

  const { data: watchlistData, refetch: refetchWatchlistData, isLoading: isWatchlistLoading } = useWatchlistData(
    loggedInUser?.email || ''
  );
  const {
    data: userListsData,
    refetch: refetchUserLists,
    deleteList: deleteListMutation,
    isLoading: isListsLoading,
    error: listsError
  } = useUserLists(loggedInUser?.email);

  // Watchlist labels (sub-watchlists)
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(loggedInUser?.email);
  // Strategy tabs (issue #337): the watches endpoint is the membership record —
  // strategies[] with counts, watches[] with strategy_id. Same query key as the
  // Strategies widget and the My Lists dropdown, so one fetch serves all three.
  const { data: signalWatchData, isLoading: isSignalWatchLoading } = useSignalWatches(!!loggedInUser?.email, loggedInUser?.email);
  const strategies = signalWatchData?.strategies;

  // Label manager modal state
  const [labelManagerModal, setLabelManagerModal] = useState({ isOpen: false, mode: 'list' });
  const [purchaseInfoModal, setPurchaseInfoModal] = useState({ isOpen: false, symbol: null, label: null, currentPrice: null });

  // Add logging to track userListsData changes
  useEffect(() => {
    console.log('userListsData updated:', userListsData);
  }, [userListsData]);

  // Journey tracker initialization
  useEffect(() => {
    journeyTracker.init();

    // Track first scroll
    let hasScrolled = false;
    const handleScroll = () => {
      if (!hasScrolled) {
        hasScrolled = true;
        journeyTracker.trackScrollStart();
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      journeyTracker.destroy();
    };
  }, []);

  // Update journey tracker with user ID when logged in
  useEffect(() => {
    if (loggedInUser?.email) {
      journeyTracker.setUserId(loggedInUser.email);
    } else {
      journeyTracker.clearUserId();
    }
  }, [loggedInUser]);

  const updateUserWatchlist = useCallback(async (symbols) => {
    if (!loggedInUser) return;
    try {
      await updateWatchlist(loggedInUser, symbols);
      await refetchWatchlistData();
    } catch (error) {
      console.error('Error updating user watchlist:', error);
      setError('Failed to update watchlist. Please try again.');
    }
  }, [loggedInUser, refetchWatchlistData]);

  // Handle ?addToWatchlist=SYMBOL URL param (for extension integration)
  useEffect(() => {
    if (!addToWatchlistSymbol || !loggedInUser?.email) return;

    // Wait for watchlist data to be loaded
    if (isWatchlistLoading) return;

    const currentSymbols = watchlistData?.companiesSymbols ||
      JSON.parse(localStorage.getItem('companiesSymbols') || '[]');

    // Check if symbol already in watchlist
    if (currentSymbols.includes(addToWatchlistSymbol)) {
      console.log('[URL-ADD-WATCHLIST] Symbol already in watchlist:', addToWatchlistSymbol);
      setAddToWatchlistSymbol(null);
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('addToWatchlist');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    // Add symbol to watchlist
    console.log('[URL-ADD-WATCHLIST] Adding symbol to watchlist:', addToWatchlistSymbol);
    const newSymbols = [addToWatchlistSymbol, ...currentSymbols];

    // Update local storage
    localStorage.setItem('companiesSymbols', JSON.stringify(newSymbols));
    setCompaniesSymbols(newSymbols);

    // Sync to server
    updateWatchlist(loggedInUser, newSymbols)
      .then(() => {
        console.log('[URL-ADD-WATCHLIST] Successfully added to watchlist');
        refetchWatchlistData();
      })
      .catch(err => console.error('[URL-ADD-WATCHLIST] Error:', err));

    // Switch to watchlist tab
    setSelectedTab('watchlist');

    // Clear the state and URL
    setAddToWatchlistSymbol(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('addToWatchlist');
    window.history.replaceState({}, '', url.toString());

  }, [addToWatchlistSymbol, loggedInUser, isWatchlistLoading, watchlistData, refetchWatchlistData]);

  // Helper to show sync conflict dialog and wait for user choice
  const showSyncConflictDialog = useCallback((localSymbols, serverSymbols, localOnlySymbols, serverOnlySymbols) => {
    return new Promise((resolve) => {
      syncResolveRef.current = resolve;
      setSyncConflictData({
        localSymbols,
        serverSymbols,
        localOnlySymbols,
        serverOnlySymbols
      });
    });
  }, []);

  // Handle sync conflict resolution from dialog
  const handleSyncConflictResolve = useCallback(({ resolution, rememberChoice }) => {
    console.log('[SYNC] User chose resolution:', resolution, 'remember:', rememberChoice);

    // Save preference if requested
    if (rememberChoice) {
      localStorage.setItem(SYNC_PREFERENCE_KEY, resolution);
    }

    // Clear dialog state
    setSyncConflictData(null);

    // Resolve the promise
    if (syncResolveRef.current) {
      syncResolveRef.current(resolution);
      syncResolveRef.current = null;
    }
  }, []);

  const syncUserData = useCallback(async (user) => {
    if (!user || isSyncing.current) return;
    isSyncing.current = true;
    console.log('[SYNC] Starting user data sync for:', user.email);

    try {
      const result = await refetchWatchlistData();
      console.log('[SYNC] Fetched user data:', result?.data?.companiesSymbols?.length, 'symbols');

      // Get server and local symbols
      const serverSymbols = result?.data?.companiesSymbols || [];
      const localSymbols = JSON.parse(localStorage.getItem('companiesSymbols') || '[]');

      // Detect conflicts
      const localOnlySymbols = localSymbols.filter(s => !serverSymbols.includes(s));
      const serverOnlySymbols = serverSymbols.filter(s => !localSymbols.includes(s));
      const hasConflict = localOnlySymbols.length > 0 || serverOnlySymbols.length > 0;

      console.log('[SYNC] Conflict detection:', {
        localCount: localSymbols.length,
        serverCount: serverSymbols.length,
        localOnly: localOnlySymbols.length,
        serverOnly: serverOnlySymbols.length,
        hasConflict
      });

      let resolvedSymbols;

      if (hasConflict) {
        // Check for saved preference
        const savedPreference = localStorage.getItem(SYNC_PREFERENCE_KEY);
        let resolution;

        if (savedPreference) {
          console.log('[SYNC] Using saved preference:', savedPreference);
          resolution = savedPreference;
        } else {
          // Show dialog and wait for user choice
          console.log('[SYNC] Showing conflict dialog');
          resolution = await showSyncConflictDialog(
            localSymbols,
            serverSymbols,
            localOnlySymbols,
            serverOnlySymbols
          );
        }

        // Apply resolution
        switch (resolution) {
          case SYNC_RESOLUTION.CLOUD:
            resolvedSymbols = serverSymbols;
            console.log('[SYNC] Applied CLOUD resolution');
            break;
          case SYNC_RESOLUTION.DEVICE:
            resolvedSymbols = localSymbols;
            console.log('[SYNC] Applied DEVICE resolution');
            break;
          case SYNC_RESOLUTION.MERGE:
          default:
            resolvedSymbols = Array.from(new Set([...serverSymbols, ...localSymbols]));
            console.log('[SYNC] Applied MERGE resolution');
            break;
        }
      } else {
        // No conflict - use server data (they're the same)
        resolvedSymbols = serverSymbols;
        console.log('[SYNC] No conflict, using server data');
      }

      // Handle settings merge (excluding lists)
      const serverSettings = result?.data?.preferences || {};
      const localSettings = loadSettings();

      // Don't merge lists - only use server lists for the logged-in user
      const { lists: _, ...otherServerSettings } = serverSettings;
      const { lists: __, ...otherLocalSettings } = localSettings;

      const unmigratedSettings = {
        ...otherLocalSettings,
        ...otherServerSettings
      };
      // Server preferences saved before the sectors filter only carry industriesFilters
      const mergedSettings = migrateIndustriesToSectors(unmigratedSettings);

      // Handle notes merge
      const serverNotes = result?.data?.stockNotes || {};
      const localNotes = JSON.parse(localStorage.getItem('stockNotes') || '{}');

      // Merge notes, preferring server values for any conflicts
      const mergedNotes = { ...localNotes, ...serverNotes };

      // Update local state
      setCompaniesSymbols(resolvedSymbols);
      setUserSettings(mergedSettings); // Lists will be handled by useUserLists hook
      // Apply the synced sectors (migrated or not) so the filter and screen requests match them
      if (Array.isArray(mergedSettings.sectorsFilters)) {
        setActiveFilters(prev => ({ ...prev, sectors: mergedSettings.sectorsFilters }));
      }
      setStockNotes(mergedNotes); // Update notes state

      // Update local storage
      localStorage.setItem('companiesSymbols', JSON.stringify(resolvedSymbols));
      localStorage.setItem('userSettings', JSON.stringify(mergedSettings));
      localStorage.setItem('stockNotes', JSON.stringify(mergedNotes));

      // Use UUID when syncing back to server
      await updateUserData({
        id: user.id,    // Prefer UUID
        email: user.email // Fallback
      }, {
        preferences: mergedSettings,
        companiesSymbols: resolvedSymbols,
        stockNotes: mergedNotes
      });

    } catch (error) {
      console.error('[SYNC] Error in syncUserData:', error);
    } finally {
      isSyncing.current = false;
    }
  }, [refetchWatchlistData, updateUserData, showSyncConflictDialog]);

  // Add this helper function to merge user lists
  const mergeUserLists = (localLists, serverLists) => {
    const mergedLists = [...serverLists];
    
    localLists.forEach(localList => {
      const existingList = mergedLists.find(serverList => serverList.id === localList.id);
      
      if (!existingList) {
        // If local list doesn't exist on server, add it
        mergedLists.push(localList);
      } else {
        // If list exists, merge symbols
        const mergedSymbols = Array.from(new Set([
          ...(existingList.symbols || []),
          ...(localList.symbols || [])
        ]));
        
        // Update existing list with merged data
        Object.assign(existingList, {
          ...localList,
          symbols: mergedSymbols,
          updated_at: new Date().toISOString()
        });
      }
    });
    
    return mergedLists;
  };

  // Modify handleSettingsChange to handle lists separately
  const handleSettingsChange = useCallback((key, value) => {
    setUserSettings(prev => {
      const updated = {
        ...prev,
        [key]: value
      };
      localStorage.setItem('userSettings', JSON.stringify(updated));

      // If this is a lists update, don't sync it through user preferences
      if (key === 'lists' && loggedInUser?.email) {
        // Lists are handled by the useUserLists hook and its mutations
        return updated;
      }

      // For other settings, sync to server
      if (loggedInUser?.email) {
        const { lists, ...settingsWithoutLists } = updated;
        updateUserData(loggedInUser.email, {
          preferences: settingsWithoutLists
        }).catch(error => {
          console.error('Error syncing settings to server:', error);
        });
      }

      return updated;
    });
  }, [loggedInUser]);

  // Which Brief sections this user wants. Persisted through
  // handleSettingsChange rather than written to users.preferences directly: that
  // column is replaced as a WHOLE OBJECT by fp_user and again here from the
  // `userSettings` snapshot, so a key set by anything other than this writer is
  // wiped the next time the user touches a filter. Going through it keeps the map
  // inside the snapshot that every later write spreads.
  const mobileFeedSections = userSettings?.mobileFeedSections || EMPTY_FEED_SECTIONS;

  const handleToggleFeedSection = useCallback((key) => {
    // Read the current map from the closure, never from inside a setState updater:
    // handleSettingsChange writes to localStorage and POSTs to the server, and a
    // React updater must stay pure — StrictMode double-invokes it, which would
    // fire two saves per tap.
    const current = userSettings?.mobileFeedSections || {};
    // Store the explicit boolean both ways. Deleting a `false` back to absent would
    // work today, but absence means "on" (so a section added later shows up for
    // existing users) and an explicit `true` records that the user has seen it.
    handleSettingsChange('mobileFeedSections', {
      ...current,
      [key]: !isFeedSectionEnabled(current, key),
    });
  }, [handleSettingsChange, userSettings]);

  const hiddenFeedSectionCount =
    MOBILE_FEED_SECTIONS.length - countEnabledSections(mobileFeedSections);

  const handleSetAllFeedSections = useCallback((enabled) => {
    handleSettingsChange(
      'mobileFeedSections',
      Object.fromEntries(MOBILE_FEED_SECTIONS.map((section) => [section.key, enabled]))
    );
  }, [handleSettingsChange]);

  // Your existing handleSettingsSave function
  const handleSettingsSave = useCallback((value, category) => {
    const updatedSettings = saveSettings(category, value);
    setUserSettings(updatedSettings);
    
    // If user is logged in, sync to server
    if (loggedInUser) {
      updateUserData(loggedInUser, { 
        preferences: updatedSettings 
      }).catch(error => {
        console.error('Error saving settings:', error);
      });
    }
    
    return updatedSettings;
  }, [loggedInUser]);

  const queryClient = useQueryClient();

  queryClient.setQueryDefaults(['marketAnalysis'], {
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  

  const {
    data: allEconomicEvents,
    isLoading: isLoadingEconomicEvents,
    error: economicError,
  } = useEconomicData(geography);
  const { data: marketAnalysisData } = useMarketAnalysis();
  const { data: mobileFearGreedData } = useQuery({
    queryKey: ['fearAndGreed'],
    queryFn: async () => {
      const response = await publicFetch('https://api.1stprinciples.pro/fear-greed?full=true');
      if (!response.ok) {
        throw new Error('Failed to fetch fear & greed data');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    enabled: isMobile,
  });
  const { data: mobileMarketStats } = useQuery({
    queryKey: ['dailyMovementAnalytics'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.DAILY_MOVEMENT_ANALYTICS);
      if (!response.ok) {
        throw new Error('Failed to fetch market breadth data');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    enabled: isMobile,
  });
  const { data: mobileSectorRotationData } = useQuery({
    queryKey: ['sectorRotation'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.SECTOR_ROTATION);
      if (!response.ok) {
        throw new Error('Failed to fetch sector rotation data');
      }
      return response.json();
    },
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
    enabled: isMobile,
  });
  const { data: mobileMarketOverviewData } = useQuery({
    queryKey: ['marketOverview'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.MARKET_OVERVIEW);
      if (!response.ok) {
        throw new Error('Failed to fetch market overview');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Poll every 5 minutes
    refetchOnWindowFocus: true,
    enabled: isMobile,
  });
  const { data: mobileMacroScenariosData } = useQuery({
    queryKey: ['macroScenarios'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.MACRO_SCENARIOS);
      if (!response.ok) {
        throw new Error('Failed to fetch macro scenarios');
      }
      return response.json();
    },
    staleTime: 12 * 60 * 60 * 1000,
    refetchInterval: 12 * 60 * 60 * 1000,
    enabled: isMobile,
  });
  const { data: mobileCurvesData } = useQuery({
    queryKey: ['curvesData'],
    queryFn: async () => {
      const response = await fetch(CONFIG.API_URLS.CURVES_DATA);
      if (!response.ok) {
        throw new Error('Failed to fetch bond curve data');
      }
      return response.json();
    },
    staleTime: 60 * 60 * 1000,
    refetchInterval: 6 * 60 * 60 * 1000,
    enabled: isMobile,
  });

  /**
   * Tickers in the current list that reported recently enough for their call
   * takeaway to still be news.
   *
   * `last_earnings_date` is the right column here: `next_earnings_date` rolls to the
   * following quarter the moment a company reports, so it can never answer "who just
   * reported". Parsed off the date PREFIX rather than through `new Date(str)` —
   * a date-only string parses as UTC midnight while `.getMonth()` reports local time,
   * which lands a day early for every negative-offset user.
   *
   * Capped so the query key stays stable-ish and the worker's IN-list stays small; the
   * feed only ever renders a handful of these anyway.
   */
  const mobileRecentEarningsTickers = useMemo(() => {
    if (!isMobile || !Array.isArray(companiesData) || !companiesData.length) {
      return [];
    }
    const cutoff = new Date();
    cutoff.setUTCDate(cutoff.getUTCDate() - CALL_HIGHLIGHT_LOOKBACK_DAYS);
    const cutoffKey = cutoff.toISOString().slice(0, 10);

    return companiesData
      .map((company) => {
        const raw = company?.lastEarningsDate;
        const day = typeof raw === 'string' ? raw.slice(0, 10) : '';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day < cutoffKey) {
          return null;
        }
        return { symbol: company.tickerSymbol, day };
      })
      .filter(Boolean)
      .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
      .slice(0, MAX_CALL_HIGHLIGHT_TICKERS)
      .map((entry) => entry.symbol);
  }, [isMobile, companiesData]);

  // Batched: one request for every recent reporter, not one per ticker. The hook is
  // disabled without an email, so logged-out users simply get no call items.
  const { data: mobileCallHighlights } = useCallHighlights(
    mobileRecentEarningsTickers,
    loggedInUser?.email
  );

  // Why watchlist stocks moved 5%+ (issue #317): one batched request for the whole watchlist,
  // read by the movers cards (explanation as the card text) and the shared-driver cards.
  const { data: mobileMoverNews } = useMoverNews(isMobile ? companiesSymbols : []);

  // Add toast state for notifications
  const [toast, setToast] = useState({
    message: '',
    type: 'info',
    visible: false
  });
  
  // Make sure the showNotification function is defined at the top level of the App component
  const showNotification = useCallback(({ message, type = 'info', duration = 3000 }) => {
    setToast({
      message,
      type,
      visible: true
    });
    
    // Auto-hide the notification after the specified duration
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, duration);
  }, []);

  // Define handleCustomFilterFetch before loadData
  const handleCustomFilterFetch = useCallback(async (filterId, userId) => {
    try {
      console.log('handleCustomFilterFetch called with filterId:', filterId, 'and userId:', userId);
      
      // Use the imported service function
      const symbols = await fetchCustomFilterResults(filterId, userId);
      console.log('fetchCustomFilterResults returned symbols:', symbols?.length || 0);
      return symbols;
    } catch (error) {
      console.error('Error in handleCustomFilterFetch:', error);
      showNotification({
        message: `Error fetching custom filter: ${error.message}`,
        type: 'error'
      });
      return [];
    }
  }, [showNotification]);

  // 1. Add a ref to track loading state
  const isLoadingRef = useRef(false);
  // Issue #396: bumped by every foreground load, so a background refresh that finishes after
  // the user moved on can tell its rows belong to a list that is no longer on screen.
  const loadGenerationRef = useRef(0);

  // 2. Modify loadData to use the new loading control
  // `silent` is the market-hours background refresh (issue #396): no spinner, and it stays out
  // of loadingControl so it can never swallow a real load (loadingControl drops any call made
  // while one runs) — a real load in progress makes IT skip instead. Anything that goes wrong
  // keeps the rows on screen, and its result is dropped if a foreground load started meanwhile.
  const loadData = useCallback(async (forceRefresh = false, { silent = false } = {}) => {
    if (silent) {
      if (loadingControl.current) return;
    } else {
      if (!forceRefresh && loadingControl.isThrottled()) {
        console.log('[WATCHLIST-DEBUG] Load skipped - throttled');
        return;
      }

      if (!loadingControl.start()) {
        console.log('[WATCHLIST-DEBUG] loadData skipped - loadingControl.start() returned false');
        return;
      }
    }
    const generation = silent ? loadGenerationRef.current : ++loadGenerationRef.current;
    // The one exit for "nothing to show"; a background refresh keeps what is on screen instead.
    const showEmpty = (message) => {
      if (silent) {
        console.log(`[AUTO-REFRESH] ${selectedTab}: kept current rows (${message})`);
        return;
      }
      setError(message);
      setCompaniesData([]);
    };

    console.log('Loading data for tab:', selectedTab);

    // AI Portfolios are rendered by PortfolioView, skip screen data loading
    if (selectedTab?.startsWith('ptf_')) {
      if (!silent) loadingControl.end();
      return;
    }

    try {
      if (!silent) setIsLoading(true);
      let rawData;
      
      // Find a matching list from userListsData (could be AI, portfolio, or custom filter)
      const selectedList = userListsData?.find((list) => list.id === selectedTab);
      console.log('Selected list:', selectedList);

      // Check if this is a custom filter
      if (selectedList?.type === 'custom_filter') {
        console.log('Loading custom filter:', selectedList.name);
        
        // Get the user ID from the authenticated user
        const currentUser = loggedInUser;
        const userId = currentUser?.id || currentUser?.email;
        if (!userId) {
          showEmpty('You must be logged in to use custom filters');
          return;
        }
        
        try {
          // Use the handleCustomFilterFetch function to get the symbols
          const filterSymbols = await handleCustomFilterFetch(selectedTab, userId);
          console.log('Custom filter returned symbols:', filterSymbols?.length || 0);
          
          if (filterSymbols && filterSymbols.length > 0) {
            // Fetch data for the symbols
            rawData = await fetchScreenData(null, null, filterSymbols, activeFilters);
            
            if (!rawData || rawData.length === 0) {
              showEmpty(`No stocks match the custom filter "${selectedList.name}" with the current filters.`);
              return;
            }
          } else {
            showEmpty('No stocks match the custom filter criteria');
            return;
          }
        } catch (error) {
          console.error('Error processing custom filter:', error);
          showEmpty(`Error with custom filter: ${error.message}`);
          return;
        }
      } else if (selectedList?.type === 'ai_filter' || selectedList?.type === 'portfolio') {
        // For AI or portfolio lists, fetch by list's symbols
        if (selectedList?.symbols) {
          let symbolsArray;
          try {
            symbolsArray = typeof selectedList.symbols === 'string' 
              ? JSON.parse(selectedList.symbols)
              : selectedList.symbols;
            
            if (!Array.isArray(symbolsArray)) {
              console.error('Invalid symbols format:', selectedList.symbols);
              symbolsArray = [];
            }
          } catch (error) {
            console.error('Error parsing symbols:', error);
            symbolsArray = [];
          }
          
          if (symbolsArray.length > 0) {
            rawData = await fetchScreenData(null, null, symbolsArray, activeFilters);
          } else {
            showEmpty('No symbols found in this list.');
            return;
          }
        }
      } else if (selectedTab === 'url_symbols') {
        // For URL-based symbol lists (extension integration)
        if (urlSymbols && urlSymbols.length > 0) {
          console.log('[URL-SYMBOLS] Loading data for symbols:', urlSymbols);
          rawData = await fetchScreenData(null, null, urlSymbols, activeFilters);
          if (!rawData || rawData.length === 0) {
            showEmpty('No valid stocks found from the provided symbols.');
            return;
          }
        } else {
          showEmpty('No symbols provided in URL.');
          return;
        }
      } else if (selectedTab?.startsWith('symbols_')) {
        // Ad-hoc symbol set (e.g. a Signal Watches ticker that isn't in any
        // currently loaded list) — see the virtual-tab pattern in CLAUDE.md.
        const adHocSymbols = selectedTab.slice('symbols_'.length).split(',').filter(Boolean);
        console.log('[SYMBOLS-TAB] Loading symbols:', adHocSymbols);
        rawData = await fetchScreenData(null, null, adHocSymbols, activeFilters);
        if (!rawData || rawData.length === 0) {
          showEmpty(`No data found for ${adHocSymbols.join(', ')}.`);
          return;
        }
      } else if (selectedTab?.startsWith('earnings_day_')) {
        // Histogram bar click: load that day's reporters into the table
        const day = selectedTab.replace('earnings_day_', '');
        const resp = await publicFetch(`${CONFIG.API_URLS.EARNINGS_CALENDAR}?date=${day}&limit=500&symbols_only=1`);
        const json = await resp.json();
        const daySymbols = json.symbols || [];
        console.log('[EARNINGS-DAY] Loading table for', day, '-', daySymbols.length, 'symbols');
        if (daySymbols.length > 0) {
          rawData = await fetchScreenData(null, null, daySymbols, activeFilters);
        } else {
          showEmpty(`No companies found reporting on ${day}.`);
          return;
        }
      } else if (isStrategyTab(selectedTab)) {
        // Strategy tabs (issue #337): the stocks opted into the strategy via a
        // signal watch. Membership comes from the watches endpoint, resolved by
        // the same ownedByStrategy predicate the Strategies widget counts with.
        const strategy = findStrategyForTab(strategies, selectedTab);
        const memberSymbols = strategy ? strategySymbols(signalWatchData?.watches, strategy, strategies) : [];
        if (memberSymbols.length > 0) {
          console.log('[STRATEGY-TAB] Loading stocks for strategy:', strategy.id, memberSymbols);
          rawData = await fetchScreenData(null, null, memberSymbols, activeFilters);
        } else {
          showEmpty(strategy
            ? `No stocks in ${strategy.name} yet — open a stock's Trading Range chart and click 🔔 Watch to add one.`
            : (loggedInUser ? 'Strategy not found.' : 'Sign in to see your strategies.'));
          return;
        }
      } else if (selectedTab?.startsWith('label_')) {
        // For label tabs, load data for all stocks with this label
        const labelId = selectedTab.replace('label_', '');
        const labelSymbols = Object.entries(labelsData?.assignments || {})
          .filter(([_, assignment]) => assignment?.labels?.includes(labelId))
          .map(([symbol]) => symbol);

        if (labelSymbols.length > 0) {
          console.log('[LABEL-DEBUG] Loading stocks for label:', labelId, labelSymbols);
          rawData = await fetchScreenData(null, null, labelSymbols, activeFilters);
        } else {
          showEmpty('No stocks in this label yet.');
          return;
        }
      } else if (selectedTab === 'watchlist') {
        // For watchlist tab, load watchlist data
        if (loggedInUser) {
          // Use cached watchlistData if available, only refetch if empty/stale
          if (watchlistData?.companies?.length > 0 && !forceRefresh) {
            console.log('[WATCHLIST-DEBUG] Using cached watchlistData');
            rawData = watchlistData.companies;
          } else {
            console.log('[WATCHLIST-DEBUG] Refetching watchlistData (no cache or forceRefresh)');
            const result = await refetchWatchlistData();
            rawData = result?.data?.companies || [];
          }
        } else {
          const localSymbols = JSON.parse(localStorage.getItem('companiesSymbols') || '[]');
          if (localSymbols.length > 0) {
            rawData = await fetchScreenData('watchlist', null, localSymbols, activeFilters);
          } else {
            showEmpty('Your watchlist is empty. Add some symbols to get started!');
            return;
          }
        }
      } else {
        try {
          rawData = await fetchScreenData(selectedTab, null, null, activeFilters);
        } catch (error) {
          // Handle specific API errors
          if (error.message?.includes('No symbols found matching the criteria')) {
            showEmpty('No stocks match the current filter criteria. Try adjusting your filters.');
            return;
          }
          throw error; // Re-throw other errors to be caught by outer catch block
        }
      }

      console.log('[WATCHLIST-DEBUG] rawData after fetch:', {
        length: rawData?.length,
        hasData: !!rawData,
        firstItem: rawData?.[0]?.tickerSymbol
      });

      if (!rawData || rawData.length === 0) {
        const category = categories?.find(cat => cat.id === selectedTab)?.name || selectedTab;
        showEmpty(`No stocks found for ${category}. Try adjusting your filters or check back later.`);
        return;
      }

      if (DEBUG.filters) {
        console.log('Raw data loaded:', {
          selectedTab,
          rawLength: rawData.length,
          firstSymbol: rawData[0]?.tickerSymbol || rawData[0]?.symbol,
          activeMarketCapFilters: activeFilters.marketCap,
          activeSectorsFilterCount: activeFilters.sectors?.length || 0,
          notInterestedCount: notInterestedSymbols.length
        });
      }

      const filteredData = filterCompaniesByMarketCap(rawData || [], activeFilters.marketCap);
      if (DEBUG.filters) {
        const sampleCap = filteredData[0]?.marketCap;
        const sampleTicker = filteredData[0]?.tickerSymbol || filteredData[0]?.symbol;
        console.log('Market cap filter result count:', filteredData.length, {
          sampleTicker,
          sampleCap
        });
      }
      if (filteredData.length === 0) {
        showEmpty('No stocks match the current market cap filter. Try adjusting your filters.');
        return;
      }
      
      // Filter out not interested companies
      const filteredDataFiltered = filteredData.filter(company => !notInterestedSymbols.includes(company.tickerSymbol));
      if (DEBUG.filters) {
        const sampleTickerAfter = filteredDataFiltered[0]?.tickerSymbol || filteredDataFiltered[0]?.symbol;
        console.log('After not interested filter count:', filteredDataFiltered.length, {
          sampleTickerAfter
        });
      }
      
      if (silent && generation !== loadGenerationRef.current) {
        console.log(`[AUTO-REFRESH] ${selectedTab}: dropped, a newer load started`);
        return;
      }
      setCompaniesData(filteredDataFiltered);
      setError(''); // Clear any previous errors
      
    } catch (error) {
      console.error('Error loading data:', error);
      // Create user-friendly error message
      let errorMessage = 'Unable to load stock data. ';
      if (error.message?.includes('market cap range')) {
        errorMessage += 'Please check your market cap filter settings.';
      } else if (error.message?.includes('industry filter')) {
        errorMessage += 'Please check your industry filter settings.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else {
        errorMessage += 'Please try again later.';
      }
      showEmpty(errorMessage);
    } finally {
      if (!silent) {
        console.log('[WATCHLIST-DEBUG] loadData finally block - setting isLoading=false');
        loadingControl.end();
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    }
  }, [selectedTab, activeFilters.marketCap, userListsData, loggedInUser, notInterestedSymbols, handleCustomFilterFetch, watchlistData, labelsData, urlSymbols, signalWatchData, strategies]);

  // Removed redundant filter effect - activeFilters.marketCap and activeFilters.sectors are already dependencies of the main loadData effect

  const handleFilterApply = useCallback((filters) => {
    console.log('Filter apply start with:', filters);
    setActiveFilters(prevFilters => {
      const nextFilters = { ...prevFilters };

      if (filters.types !== undefined) {
        nextFilters.types = filters.types;
      }

      if (filters.marketCap !== undefined) {
        nextFilters.marketCap = filters.marketCap;
      }

      if (filters.sectors !== undefined) {
        nextFilters.sectors = filters.sectors === null ? [] : filters.sectors;
      }

      return nextFilters;
    });

    // Save type settings if they were updated
    if (filters.types !== undefined) {
      const updatedSettings = saveSettings('typeFilters', filters.types);
      // Keep userSettings current so later preference writes don't send a stale snapshot
      setUserSettings(updatedSettings);

      if (loggedInUser) {
        updateUserData(loggedInUser, {
          preferences: {
            ...userSettings,
            ...updatedSettings
          }
        });
      }
    }

    // Save market cap settings if they were updated
    if (filters.marketCap !== undefined) {
      const updatedSettings = saveSettings('marketCapFilters', filters.marketCap);
      setUserSettings(updatedSettings);

      if (loggedInUser) {
        updateUserData(loggedInUser, {
          preferences: {
            ...userSettings,
            ...updatedSettings
          }
        });
      }
    }

    // Save sectors settings if they were updated
    if (filters.sectors !== undefined) {
      const sectorsSelection = filters.sectors === null ? [] : filters.sectors;
      const updatedSettings = saveSettings('sectorsFilters', sectorsSelection);
      setUserSettings(updatedSettings);

      if (loggedInUser) {
        updateUserData(loggedInUser, {
          preferences: {
            ...userSettings,
            ...updatedSettings
          }
        });
      }
    }
  }, [loggedInUser, userSettings]);

  // Data loading effect
  useEffect(() => {
    // For watchlist tab with logged in user, wait for React Query to finish loading
    // This prevents double-fetching (React Query fetch + loadData refetch)
    if (selectedTab === 'watchlist' && loggedInUser && isWatchlistLoading) {
      console.log('[WATCHLIST-DEBUG] Waiting for React Query to finish loading');
      return;
    }
    // Same wait for a strategy tab (issue #337): its members come from the watches query,
    // and this effect is what invokes loadData. loadingControl drops any call within 1s of
    // the previous one, so a premature pass is not merely wasted — it can swallow the real
    // one: the first pass ran before the session was restored (checkAuth is async), set the
    // "sign in" empty state, and the pass that followed the watches landing was throttled
    // away. So wait (a) while a stored token means a session restore is still in flight,
    // and (b) while the watches query has not delivered. Structural sharing keeps
    // signalWatchData's identity across refetches with identical content, so a background
    // refetch does not reload the table; a membership change does.
    if (isStrategyTab(selectedTab)) {
      const restorePending = !loggedInUser && !!localStorage.getItem('authToken');
      const watchesPending = loggedInUser && (isSignalWatchLoading || signalWatchData === undefined);
      if (restorePending || watchesPending) {
        console.log('[STRATEGY-TAB] Waiting before loading members', { restorePending, watchesPending: !!watchesPending });
        return;
      }
    }

    console.log('[WATCHLIST-DEBUG] loadData effect triggered', {
      selectedTab,
      loggedInUser: !!loggedInUser,
      isWatchlistLoading,
      typeFilters: activeFilters.types,
      marketCapFilters: activeFilters.marketCap,
      sectorsFilters: activeFilters.sectors
    });

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, activeFilters.types, activeFilters.marketCap, activeFilters.sectors, isWatchlistLoading, loggedInUser, isSignalWatchLoading, signalWatchData]);

  // Issue #396: keep the table's prices and columns current while the page stays open through
  // the trading day. Switching lists restarts the clock — that load is fresh already.
  useMarketHoursRefresh(() => loadData(true, { silent: true }), selectedTab);

  const debouncedSync = useCallback(
    debounce((email, data) => {
      if (JSON.stringify(data) !== JSON.stringify(lastSyncedData.current)) {
        syncUserData(email, data);
        lastSyncedData.current = data;
      }
    }, 1000),
    []
  );

  useEffect(() => {
    // Skip sync while symbols are being added (prevents server overwriting local changes)
    if (addingSymbolsRef.current.size > 0 || isBatchAddingRef.current) {
      return;
    }
    if (loggedInUser && companiesSymbols.length > 0) {
      debouncedSync(loggedInUser, companiesSymbols);
    }
  }, [loggedInUser, companiesSymbols, debouncedSync]);

  const handleAddToWatchList = useCallback(async (companyOrSymbol, options = {}) => {
    const { skipServerSync = false, isBatchOperation = false } = options;

    // Set batch flag if this is part of a batch operation
    if (isBatchOperation) {
      isBatchAddingRef.current = true;
    }

    const tickerSymbol = typeof companyOrSymbol === 'string'
      ? companyOrSymbol.trim().toUpperCase()
      : companyOrSymbol.tickerSymbol;

    if (!tickerSymbol) {
      setError('Invalid symbol. Please enter a valid stock symbol.');
      setStatusType('error');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Check if already being added (prevents race condition duplicates)
    if (addingSymbolsRef.current.has(tickerSymbol)) {
      return; // Already in progress, skip silently
    }

    // Mark as being added
    addingSymbolsRef.current.add(tickerSymbol);

    // Use functional update to ensure we always work with latest state
    let alreadyExists = false;

    setCompaniesSymbols(prevSymbols => {
      if (prevSymbols.includes(tickerSymbol)) {
        alreadyExists = true;
        addingSymbolsRef.current.delete(tickerSymbol);
        return prevSymbols; // Don't modify if already exists
      }
      const updatedSymbols = [...prevSymbols, tickerSymbol];
      // Update localStorage synchronously within the update
      localStorage.setItem('companiesSymbols', JSON.stringify(updatedSymbols));
      return updatedSymbols;
    });

    // Note: alreadyExists won't be set synchronously, but the functional update
    // guarantees no duplicates. We'll skip the error message for batch operations.

    setIsLoading(true);
    try {
      // If the symbol is in the "Not Interested" list, remove it
      if (notInterestedSymbols.includes(tickerSymbol)) {
        const updatedNotInterested = notInterestedSymbols.filter(symbol => symbol !== tickerSymbol);
        setNotInterestedSymbols(updatedNotInterested);
        localStorage.setItem('notInterestedSymbols', JSON.stringify(updatedNotInterested));
      }

      // If logged in, sync to server (skip if this is part of a batch operation - batch will sync at end)
      if (loggedInUser?.email && !skipServerSync) {
        try {
          const currentSymbols = JSON.parse(localStorage.getItem('companiesSymbols') || '[]');
          await updateWatchlist(loggedInUser, currentSymbols);
        } catch (error) {
          console.error('Server sync failed:', error);
          // Keep local changes even if server sync fails
        }
      }

      // Fetch the data for the new symbol
      const newSymbolData = await fetchScreenData(null, null, [tickerSymbol], activeFilters);
      if (newSymbolData && newSymbolData.length > 0) {
        setCompaniesData(prevData => [...prevData, newSymbolData[0]]);
      }

      setStatusMessage(`${tickerSymbol} added to watch list successfully.`);
      setStatusType('success');

    } catch (error) {
      console.error('Error adding new symbol:', error);
      setError(`Failed to add ${tickerSymbol} to watch list: ${error.message}`);
      setStatusType('error');
    } finally {
      // Remove from in-flight set
      addingSymbolsRef.current.delete(tickerSymbol);
      setIsLoading(false);
      setTimeout(() => {
        setError('');
        setStatusMessage('');
      }, 5000);
    }
  }, [companiesSymbols, loggedInUser, updateWatchlist, fetchScreenData, activeFilters, notInterestedSymbols]);

  const handleDeleteFromWatchList = useCallback(async (tickerSymbol) => {
    try {
      const updatedSymbols = companiesSymbols.filter(symbol => symbol !== tickerSymbol);
      setCompaniesSymbols(updatedSymbols);
      localStorage.setItem('companiesSymbols', JSON.stringify(updatedSymbols));

      // If logged in, sync to server
      if (loggedInUser?.email) {
        try {
          await updateWatchlist(loggedInUser, updatedSymbols);
        } catch (error) {
          console.error('Server sync failed:', error);
          // Keep local changes even if server sync fails
        }
      }

      setCompaniesData(prevData => prevData.filter(company => company.tickerSymbol !== tickerSymbol));
      setStatusMessage(`${tickerSymbol} removed from watch list.`);
      setStatusType('success');
    } catch (error) {
      console.error('Error removing symbol:', error);
      setError(`Failed to remove ${tickerSymbol} from watch list: ${error.message}`);
      setStatusType('error');
    } finally {
      setTimeout(() => {
        setError('');
        setStatusMessage('');
      }, 5000);
    }
  }, [companiesSymbols, loggedInUser, updateWatchlist]);

  const handleNotInterested = useCallback(async (tickerSymbol) => {
    console.log('handleNotInterested called with:', tickerSymbol);
    try {
      // Get current not interested symbols
      const currentNotInterested = [...notInterestedSymbols];
      console.log('Current not interested symbols:', currentNotInterested);
      
      if (currentNotInterested.includes(tickerSymbol)) {
        console.log('Symbol already in not interested list');
        return; // Already in not interested list
      }
      
      const updatedNotInterested = [...currentNotInterested, tickerSymbol];
      console.log('Updated not interested symbols:', updatedNotInterested);
      
      // Update state
      setNotInterestedSymbols(updatedNotInterested);
      
      // Update local storage
      localStorage.setItem('notInterestedSymbols', JSON.stringify(updatedNotInterested));
      console.log('Saved to localStorage:', JSON.stringify(updatedNotInterested));
      
      // If logged in, sync to server by updating preferences
      if (loggedInUser?.email) {
        try {
          // Update user settings with not interested symbols
          const updatedSettings = {
            ...userSettings,
            notInterestedSymbols: updatedNotInterested
          };
          
          // Save to local state
          setUserSettings(updatedSettings);
          
          // Sync to server
          await updateUserData(loggedInUser, {
            preferences: updatedSettings
          });
          
          console.log('Not interested symbols synced to server');
        } catch (error) {
          console.error('Server sync failed:', error);
          // Keep local changes even if server sync fails
        }
      }
      
      // If the company is in the current view, filter it out
      setCompaniesData(prevData => {
        const filtered = prevData.filter(company => company.tickerSymbol !== tickerSymbol);
        console.log(`Filtered out ${tickerSymbol}, removed ${prevData.length - filtered.length} items`);
        return filtered;
      });
      
      setStatusMessage(`${tickerSymbol} marked as not interested.`);
      setStatusType('success');
      console.log('Status message set:', `${tickerSymbol} marked as not interested.`);
    } catch (error) {
      console.error('Error marking as not interested:', error);
      setError(`Failed to mark ${tickerSymbol} as not interested: ${error.message}`);
      setStatusType('error');
    } finally {
      setTimeout(() => {
        setError('');
        setStatusMessage('');
      }, 5000);
    }
  }, [notInterestedSymbols, loggedInUser, userSettings, updateUserData]);

  const handleLoginSuccess = useCallback((userData) => {
    const user = {
      id: userData.id, // Store UUID
      email: userData.email,
      name: userData.name || userData.email.split('@')[0],
    };
    
    setLoggedInUser(user);
    // Login callbacks provide { email, name } with no id, so guard against persisting the
    // literal string "undefined". The real UUID is stored by updateUserData() from the
    // server response once syncUserData resolves; clear any stale value until then.
    if (userData.id) {
      localStorage.setItem('userId', userData.id);
    } else {
      localStorage.removeItem('userId');
    }

    // Sync by email fallback (updateUserData resolves the real UUID server-side)
    syncUserData(user);
    
    setSelectedTab('watchlist');
    setStatusMessage('Successfully logged in!');
    setStatusType('success');
    setTimeout(() => setStatusMessage(''), 5000);
  }, [syncUserData]);

  const handleLogout = useCallback((showMessage = true) => {
    // Clear user state
    setLoggedInUser(null);

    // Clear all auth tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');

    // Clear React Query cache to prevent stale data on next login
    queryClient.clear();

    // Tell the invitation gate (AccessGate) to return to the landing page — the
    // app is invite-only, so a signed-out session has nowhere to sit. This is a
    // DIFFERENT event from 'auth:logout' on purpose: App itself listens for
    // that one and responds by calling handleLogout, so reusing it here would
    // re-enter this function before the state update lands.
    window.dispatchEvent(new CustomEvent('auth:signed-out', { detail: { reason: 'user_logout' } }));

    if (showMessage) {
      setStatusMessage('You have been logged out.');
      setStatusType('info');
      setTimeout(() => setStatusMessage(''), 5000);
    }
  }, [queryClient]);

  useEffect(() => {
    const initializeUserData = async () => {
      if (loggedInUser && !isSyncing.current) {
        console.log('[WATCHLIST-DEBUG] initializeUserData starting');
        isSyncing.current = true;
        try {
          await syncUserData(loggedInUser);
          // Invalidate queries with correct keys matching api.js
          // Note: Not calling loadData() here - the main loadData effect will handle it
          // and invalidateQueries will trigger React Query auto-refetch
          await queryClient.invalidateQueries({ queryKey: ['watchlistData', loggedInUser.email] });
          await queryClient.invalidateQueries({ queryKey: ['userLists', loggedInUser.email] });
          await queryClient.invalidateQueries({ queryKey: ['notificationSubscriptions', loggedInUser.email] });

          // Load not interested symbols from user preferences
          if (userSettings?.notInterestedSymbols) {
            setNotInterestedSymbols(userSettings.notInterestedSymbols);
            localStorage.setItem('notInterestedSymbols', JSON.stringify(userSettings.notInterestedSymbols));
          }
        } catch (error) {
          if (DEBUG.sync) {
            console.error('Error initializing user data:', error);
          }
          setError('Failed to initialize user data. Please try again.');
        } finally {
          console.log('[WATCHLIST-DEBUG] initializeUserData finished');
          isSyncing.current = false;
        }
      }
    };

    initializeUserData();
  }, [loggedInUser]);

  // Onboarding wizard trigger - show 3 seconds after login if not completed
  useEffect(() => {
    if (!loggedInUser) return;

    // Get preferences from server data first, then fallback to localStorage
    const serverPreferences = watchlistData?.preferences || {};
    let localPreferences = {};
    try {
      const stored = localStorage.getItem('userPreferences');
      if (stored) {
        localPreferences = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[ONBOARDING] Error parsing localStorage preferences:', e);
    }

    // Merge: server takes precedence, but use local as fallback
    const preferences = {
      ...localPreferences,
      ...serverPreferences
    };

    console.log('[ONBOARDING] Merged preferences:', {
      hasLocalOnboarding: !!localPreferences?.onboarding?.completedAt,
      hasServerOnboarding: !!serverPreferences?.onboarding?.completedAt,
      finalOnboardingComplete: !!preferences?.onboarding?.completedAt
    });

    setUserPreferences(preferences);

    // Also persist the merged preferences to localStorage for consistency
    if (Object.keys(serverPreferences).length > 0) {
      localStorage.setItem('userPreferences', JSON.stringify(preferences));
    }

    // Check if onboarding has been completed (from either source)
    if (preferences?.onboarding?.completedAt) {
      console.log('[ONBOARDING] Already completed');
      return;
    }

    // Only show wizard if watchlistData has loaded (to avoid premature trigger)
    if (!watchlistData) return;

    // Show wizard after 3 seconds for new users
    const timer = setTimeout(() => {
      console.log('[ONBOARDING] Showing wizard for new user');
      setShowOnboardingWizard(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [loggedInUser, watchlistData]);

  // Onboarding completion handler
  const handleOnboardingComplete = useCallback(async ({ onboarding, marketCaps, favoriteLists }) => {
    console.log('[ONBOARDING] Completing with:', { onboarding, marketCaps, favoriteLists });

    try {
      // 1. Update user preferences with onboarding data
      const updatedPreferences = {
        ...userPreferences,
        onboarding
      };

      await updateUserData(loggedInUser, {
        preferences: updatedPreferences
      });

      // 2. Set market cap filters
      const updatedSettings = saveSettings('marketCapFilters', marketCaps);
      setUserSettings(updatedSettings);
      setActiveFilters(prev => ({
        ...prev,
        marketCap: marketCaps
      }));

      // 3. Add lists to favorites
      if (favoriteLists?.length > 0) {
        await addFavoriteLists(favoriteLists);
        queryClient.invalidateQueries(['favoriteLists', loggedInUser?.email]);
      }

      // 4. Update local state AND persist to localStorage for immediate availability on refresh
      setUserPreferences(updatedPreferences);
      localStorage.setItem('userPreferences', JSON.stringify(updatedPreferences));
      console.log('[ONBOARDING] Saved preferences to localStorage:', updatedPreferences);

      // 5. Invalidate watchlistData cache to sync with server
      queryClient.invalidateQueries(['watchlistData', loggedInUser?.email]);

      // Show success message
      setStatusMessage('Your preferences have been saved!');
      setStatusType('success');
      setTimeout(() => setStatusMessage(''), 5000);

      console.log('[ONBOARDING] Completed successfully');
    } catch (error) {
      console.error('[ONBOARDING] Error completing onboarding:', error);
      setStatusMessage('Failed to save preferences. Please try again.');
      setStatusType('error');
      setTimeout(() => setStatusMessage(''), 5000);
    }
  }, [loggedInUser, userPreferences, queryClient]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // One-time sanitize: earlier builds stored the literal string "undefined" into
        // userId (login callbacks carry no id). Nothing reads it today, but clear the
        // corrupt value so it can never leak into a `userId=` request param.
        const storedUserId = localStorage.getItem('userId');
        if (storedUserId === 'undefined' || storedUserId === 'null' || storedUserId === '') {
          localStorage.removeItem('userId');
        }

        // One-time cleanup: the retired desktop popup flow stored a short-lived
        // Google ID token as a restore fallback. authToken is the sole session
        // credential now (both flows go through LoginContext).
        localStorage.removeItem('googleToken');

        const token = localStorage.getItem('authToken');
        if (token) {
          console.log('Validating auth token...');
          const validateResponse = await fetch('https://api.1stprinciples.pro/auth/validateToken', {
            method: 'POST',
            body: token
          });

          if (validateResponse.ok) {
            const user = await validateResponse.json();
            console.log('Auth token valid, user:', user);
            setLoggedInUser(user);
            return;
          }
          console.log('Auth token invalid, removing...');
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    };

    checkAuth();
  }, []);

  // Listen for logout events from authenticatedFetch (token expiration/invalidation)
  useEffect(() => {
    const handleAuthLogout = (event) => {
      console.log('[AUTH] Logout event received:', event.detail?.reason);
      if (loggedInUser) {
        handleLogout(true); // Show message when auto-logged out
        setStatusMessage('Your session has expired. Please log in again.');
        setStatusType('warning');
      }
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, [loggedInUser, handleLogout]);

  const filteredEconomicEvents = useMemo(() => {
    if (!allEconomicEvents?.events || !showEconomicEvents) return [];
    
    // Get user's timezone offset in minutes
    const userTimezoneOffset = new Date().getTimezoneOffset();
    
    // Get current date in user's timezone
    const today = new Date();
    
    // Get the start of the current week (Sunday) in user's timezone
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Get the selected timeframes from userSettings
    const selectedTimeframes = userSettings?.economicEvents?.selectedTimeframes || [0];

    return Object.entries(allEconomicEvents.events).flatMap(([date, events]) => {
      const eventDate = new Date(date);
      if (isNaN(eventDate)) {
        // Log and skip this entry
        console.warn('Skipping invalid date:', date);
        return [];
      }

      // If date is valid, proceed
      const localEventDate = new Date(eventDate);
      localEventDate.setHours(0, 0, 0, 0);

      const timeDiff = localEventDate.getTime() - startOfWeek.getTime();
      const weekDiff = Math.floor(timeDiff / (7 * 24 * 60 * 60 * 1000));

      if (selectedTimeframes.includes(weekDiff)) {
        return events.map(event => ({
          ...event,
          date,
          timeframe: weekDiff,
          weekCalculation: {
            eventDate: localEventDate.toISOString(),
            startOfWeek: startOfWeek.toISOString(),
            weekDiff,
          },
        }));
      }
      return [];
    });
  }, [allEconomicEvents?.events, showEconomicEvents, userSettings?.economicEvents?.selectedTimeframes]);

  // Initialize columns state
  const [columns, setColumns] = useState(() => {
    const savedSettings = loadSettings();
    const savedColumnKeys = savedSettings.columns || [];
    
    // Get fixed columns first
    const fixedColumns = ALL_COLUMNS.filter(col => col.isFixed);
    const fixedKeys = fixedColumns.map(col => col.key);
    
    // Combine fixed and saved columns, maintaining order
    const allColumnKeys = [...fixedKeys, ...savedColumnKeys.filter(key => !fixedKeys.includes(key))];
    
    return allColumnKeys
      .map(key => ALL_COLUMNS.find(col => col.key === key))
      .filter(Boolean);
  });

  // Handle saving column configuration
  const handleSaveColumnConfig = useCallback(
    (newColumnKeys) => {
      // Reconstruct the columns array directly from newColumnKeys
      const newColumns = newColumnKeys
        .map((key) => ALL_COLUMNS.find((col) => col.key === key))
        .filter(Boolean);

      setColumns(newColumns);

      // Save all selected column keys to local storage
      localStorage.setItem(
        'columnsConfig',
        JSON.stringify(newColumnKeys)
      );

      handleSettingsSave(newColumnKeys, 'columns');
    },
    [handleSettingsSave]
  );

  const handleColumnReorder = useCallback((newColumns) => {
    setColumns(newColumns);

    const newColumnKeys = newColumns.map((col) => col.key);

    // Save new order to localStorage
    localStorage.setItem('columnsConfig', JSON.stringify(newColumnKeys));

    // Update user settings
    handleSettingsSave(newColumnKeys, 'columns');
  }, [handleSettingsSave]);

  // Define handlers before using them in useMemo
  const handleColorSettingsClick = useCallback(() => {
    setIsColorSettingsOpen(true);
  }, []);

  const handleColorSettingsSave = useCallback((newSettings) => {
    setColorSettings(newSettings);
    const updatedSettings = saveSettings('colorSettings', newSettings);
    setUserSettings(updatedSettings);
    
    if (loggedInUser) {
      updateUserData(loggedInUser, { 
        preferences: updatedSettings 
      }).catch((error) => {
        if (DEBUG.settings) {
          console.error('Error saving color settings:', error);
        }
      });
    }
  }, [loggedInUser]);

  // 1) Obtain categories before using them
  const { data: categories } = useCategories();
  // Shared react-query key with the list picker and the desktop Lists card (issue #341);
  // the mobile Brief ranks it through the same rankListMovers the card uses.
  const { data: listPerformance } = useListPerformance();
  // Asset-class crowding for the mobile Brief (issue #372). Desktop reaches it through the heatmap
  // tab's own use of the same hook, so the request is skipped here unless the Brief is being built.
  const { data: assetBuckets } = useAssetBuckets({ enabled: isMobile });
  // Guidance by earnings season for the mobile Brief (issue #387). Desktop reaches the same
  // query key through the Market Analysis card's own use of the hook, so it is skipped here
  // unless the Brief is being built.
  const { data: guidanceSeasons } = useGuidanceSeason({ enabled: isMobile });
  console.log('Raw categories from API:', categories);

  // URL-based routing: map /screeners/:slug to selectedTab
  useEffect(() => {
    if (!categories) return; // Wait for categories to load

    const path = window.location.pathname;

    // Handle /screeners/:slug routes
    if (path.startsWith('/screeners/')) {
      const slug = path.replace('/screeners/', '').toLowerCase();

      // Find matching filter across all categories
      for (const category of categories) {
        const filter = category.filters?.find(f => {
          // Convert filter ID to slug format for comparison
          const filterSlug = f.id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          return filterSlug === slug || f.id.toLowerCase() === slug;
        });

        if (filter) {
          console.log('[URL-ROUTE] Matched slug to filter:', slug, '->', filter.id);
          setSelectedTab(filter.id);
          return;
        }
      }
      console.log('[URL-ROUTE] No match found for slug:', slug);
    }
    // Handle /screeners index - default to first available screener
    else if (path === '/screeners') {
      const firstFilter = categories[0]?.filters?.[0];
      if (firstFilter) {
        setSelectedTab(firstFilter.id);
      }
    }
    // Handle /market-overview - scroll to market section
    else if (path === '/market-overview') {
      // Market overview is always visible, just ensure we're on main page
      // Could add scroll-to-section logic here if needed
      console.log('[URL-ROUTE] Market overview route');
    }
  }, [categories]); // Only run when categories load

  // 2) Now define the listExplanation after "categories" is declared
  const listExplanation = useMemo(() => {
    // Watchlist first: the categories branch below returns unconditionally, so this must not sit after it.
    if (selectedTab === 'watchlist') {
      return loggedInUser ? "Your watchlist — the tickers you follow, with today's moves, earnings and signals." : '';
    }

    // 0a. Check for URL symbols (extension integration)
    if (selectedTab === 'url_symbols' && urlSymbols) {
      return `Page Mentions: ${urlSymbols.length} stocks detected`;
    }

    // 0b. Ad-hoc symbol set (signal-watch ticker not in any loaded list)
    if (selectedTab?.startsWith('symbols_')) {
      return `Showing ${selectedTab.slice('symbols_'.length).split(',').join(', ')}`;
    }

    // 0c. Strategy tabs (issue #337): name + the same caption the dropdown row shows.
    if (isStrategyTab(selectedTab)) {
      const strategy = findStrategyForTab(strategies, selectedTab);
      if (!strategy) return '';
      return {
        name: strategy.name,
        category: 'My Lists',
        toString: () => `My Lists: ${strategy.name}`,
        description: `Stocks opted into this strategy via a signal watch — ${strategyCaption(strategy)}`,
      };
    }

    // 0. Check for label tabs first
    if (selectedTab?.startsWith('label_') && labelsData?.labels) {
      const labelId = selectedTab.replace('label_', '');
      const label = labelsData.labels.find(l => l.id === labelId);
      if (label) {
        // Return object with name for listName prop, and formatted string for display
        const description = label.trackPurchases ? 'Portfolio with purchase tracking' : 'Custom label';
        return {
          name: label.name,
          category: 'My Lists',
          toString: () => `My Lists: ${label.name}`,
          description
        };
      }
    }

    // 1. Check for active list first
    const activeList = userListsData?.find((l) => l.id === selectedTab);
    if (activeList) {
      // Handle all list types uniformly, but with type-specific priority
      if (activeList.type === 'ai_filter') {
        // For AI filters, keep special formatting
        return activeList.explanation ||
          activeList.short_description ||
          activeList.description ||
          activeList.tooltip ||
          '';
      } else if (activeList.type === 'portfolio') {
        // For portfolios, keep special formatting
        return activeList.short_description ||
          activeList.description ||
          activeList.tooltip ||
          '';
      } else if (activeList.type === 'custom_filter') {
        // For custom filters, just show the name of the list
        return `Custom filter: ${activeList.name}`;
      } else {
        // For other types, use standard priority
        return activeList.short_description ||
          activeList.description ||
          activeList.tooltip ||
          '';
      }
    }

    // 2. For predefined lists/categories, format explanation similarly
    if (categories && selectedTab) {
      const category = categories.find(cat => cat.id === selectedTab);
      if (!category) {
        const filter = findSmartList(categories, selectedTab);
        if (filter) {
          return filter.short_description ||
                 filter.description ||
                 filter.tooltip ||
                 '';
        }
      }
      return category?.short_description ||
             category?.description ||
             '';
    }

    return '';
  }, [selectedTab, userListsData, categories, labelsData, urlSymbols, loggedInUser, strategies]);

  // /list/<slug> of the current list for the header's Share button; null unless it is a smart
  // list or a public user list. Private user lists, strategies, the watchlist and the virtual
  // tabs (url_symbols, symbols_*, earnings_day_*, label_*) don't open for other people, so
  // they get no Share button.
  const listPath = useMemo(
    () => (isShareableListId(selectedTab, categories, userListsData) ? getListPath(selectedTab) : null),
    [selectedTab, categories, userListsData]
  );

  const mobileBondHighlights = useMemo(() => {
    if (!isMobile) {
      return null;
    }
    return buildBondHighlights(mobileCurvesData);
  }, [isMobile, mobileCurvesData]);

  const { items: mobileBadgeItems, hasMore: mobileHasMore } = useMemo(() => {
    if (!isMobile) {
      // Shape must match the destructure below — a bare [] leaves mobileBadgeItems
      // undefined, one `.length` read away from a crash.
      return { items: [], hasMore: false };
    }

    const summaryItems = [];
    const marketItems = [];
    const breadthItems = [];
    const sentimentItems = [];
    const cycleItems = [];
    const sectorItems = [];
    const riskItems = [];
    const macroItems = [];
    const bondItems = [];
    const newsItems = [];
    const eventItems = [];
    const stockNewsItems = [];
    const moverItems = [];
    const listMoverItems = [];
    const callHighlightItems = [];
    const portfolioEarningsItems = [];
    const buySignalItems = [];
    const sellSignalItems = [];

    const activeListName = resolveListDisplayName({
      selectedTab,
      userListsData,
      categories,
      labelsData,
      strategies,
    });

    // The watchlist has no criteria text, so its card carries a digest of the loaded rows
    // instead (issue #314); until they load it keeps the placeholder sentence. Accessors are
    // the very ones the movers / upcoming-earnings cards use, so the numbers cannot disagree.
    const watchlistDigest = selectedTab === 'watchlist'
      ? buildWatchlistDigest(companiesData, {
          changePercentOf: todaysMoveOnly(companiesData, getCompanyChangePercent),
          daysUntilEarningsOf: (company) => daysUntilEarnings(company.earningsDate),
          marketCapOf: (company) => company.marketCap,
        })
      : null;
    const summarySubtitle = watchlistDigest
      ? formatWatchlistDigest(watchlistDigest, (v) => formatPercent(v, { fractionDigits: 1 }))
      : explanationToText(listExplanation);

    if (summarySubtitle) {
      const originalTitle = activeListName || 'Current Focus';
      summaryItems.push({
        type: 'summary',
        id: 'summary-active-list',
        label: 'Active List',
        title: truncateText(originalTitle, 110),
        fullTitle: originalTitle,
        // The digest is bounded by construction (a handful of counts and tickers) and
        // truncating it mid-mover would drop the very names the card exists to show.
        subtitle: watchlistDigest ? summarySubtitle : truncateText(summarySubtitle, 120),
        fullSubtitle: summarySubtitle,
      });
    }

    if (allEconomicEvents?.analysis?.analysis) {
      const economicSummary = allEconomicEvents.analysis.analysis;
      summaryItems.push({
        type: 'summary',
        id: 'summary-economic',
        label: 'Macro Pulse',
        title: truncateText(economicSummary, 110),
        fullTitle: economicSummary,
        meta: getEconomicSentiment(allEconomicEvents.analysis),
      });
    }

    const stats = mobileMarketStats?.data;
    if (stats) {
      const numPriceIncreases = stats.numPriceIncreases ?? 0;
      const numPriceDecreases = stats.numPriceDecreases ?? 0;
      const total = numPriceIncreases + numPriceDecreases;
      if (total > 0) {
        const advancingPercent = Math.round((numPriceIncreases / total) * 100);
        const counts = `Up ${numPriceIncreases} • Down ${numPriceDecreases}`;
        const breadthMeaning = describeBreadth(advancingPercent);
        const breadthVisual = splitBarVisual(numPriceIncreases, numPriceDecreases);
        breadthItems.push({
          type: 'breadth',
          id: 'breadth-advancers',
          label: 'Market Breadth',
          title: `${advancingPercent}% Advancing`,
          fullTitle: `${advancingPercent}% Advancing`,
          subtitle: breadthMeaning || counts,
          // Expanding swaps the reading for the raw counts it came from, so the
          // number behind the sentence is always one tap away.
          fullSubtitle: breadthMeaning ? `${breadthMeaning} (${counts})` : counts,
          // the split bar prints both counts under itself, so the meta line would repeat them
          meta: !breadthVisual && breadthMeaning ? counts : undefined,
          visual: breadthVisual,
        });
      }

      // These percentages are a SHARE OF NAMES MOVING, not an average price change,
      // so they are spelled out as "80% advancing" rather than "(80%)" — the bare
      // parenthetical reads as a price move, which is a different claim entirely.
      if (Array.isArray(stats.topIndustriesIncreases) && stats.topIndustriesIncreases.length) {
        const leaders = stats.topIndustriesIncreases
          .slice(0, 5)
          .map((item) => `${item.industry} (${formatIndustryShare(item.percentage, 'up')})`)
          .join(', ');
        if (leaders) {
          sectorItems.push({
            type: 'sector',
            id: 'sector-leaders',
            label: 'Leading Industries',
            title: leaders,
            fullTitle: leaders,
            subtitle: describeIndustryShare('up'),
            fullSubtitle: describeIndustryShare('up'),
          });
        }
      }

      if (Array.isArray(stats.topIndustriesDecreases) && stats.topIndustriesDecreases.length) {
        const laggards = stats.topIndustriesDecreases
          .slice(0, 5)
          .map((item) => `${item.industry} (${formatIndustryShare(item.percentage, 'down')})`)
          .join(', ');
        if (laggards) {
          sectorItems.push({
            type: 'risk',
            id: 'sector-laggards',
            label: 'Lagging Industries',
            title: laggards,
            fullTitle: laggards,
            subtitle: describeIndustryShare('down'),
            fullSubtitle: describeIndustryShare('down'),
          });
        }
      }
    }

    if (mobileFearGreedData?.current) {
      const rating = mobileFearGreedData.current.rating || 'Neutral';
      const score = mobileFearGreedData.current.score;
      const ratingLabel = rating.charAt(0).toUpperCase() + rating.slice(1);
      const description = getFearGreedDescription(rating);
      // ONE card: the indicators are small meters inside it (shown when it is opened), not a
      // text card each — seven cards for one reading pushed everything else down the feed.
      sentimentItems.push({
        type: 'sentiment',
        id: 'sentiment-overall',
        label: 'Fear & Greed',
        title: typeof score === 'number' ? `${ratingLabel} · ${Math.round(score)}` : ratingLabel,
        subtitle: truncateText(description, 110),
        fullSubtitle: description,
        visual: sentimentVisual(mobileFearGreedData.current, mobileFearGreedData.indicators),
      });
    }

    const marketSummary = marketAnalysisData?.analysis;
    if (marketSummary?.market_summary) {
      const summaryText = marketSummary.market_summary;
      marketItems.push({
        type: 'market',
        id: 'market-summary',
        label: 'Market Summary',
        title: truncateText(summaryText, 120),
        fullTitle: summaryText,
      });
    }

    if (Array.isArray(marketSummary?.key_events)) {
      marketSummary.key_events.slice(0, 6).forEach((event, index) => {
        marketItems.push({
          type: 'market',
          id: `market-key-${index}`,
          label: 'Key Market Event',
          title: truncateText(event, 110),
          fullTitle: event,
        });
      });
    }

    if (Array.isArray(marketSummary?.sector_performance)) {
      const rankedSectors = [...marketSummary.sector_performance]
        .filter((sector) => typeof sector?.performance === 'number')
        .sort((a, b) => Math.abs(b.performance) - Math.abs(a.performance))
        .slice(0, 6);

      rankedSectors.forEach((sector, index) => {
        sectorItems.push({
          type: 'sector',
          id: `market-sector-${sector.sector}-${index}`,
          label: 'Sector Move',
          title: `${sector.sector}`,
          fullTitle: `${sector.sector}`,
          meta: formatPercent(sector.performance),
        });
      });
    }

    (marketAnalysisData?.news_items || [])
      .filter((article) => article?.title)
      .slice(0, 5)
      .forEach((article, index) => {
        const storyTitle = article.title;
        const storySource = article.source || 'Market Desk';
        marketItems.push({
          type: 'market',
          id: `market-story-${article.url || index}`,
          label: 'Top Story',
          title: truncateText(storyTitle, 100),
          fullTitle: storyTitle,
          subtitle: storySource,
          fullSubtitle: storySource,
          meta: formatRelativeTimeFromNow(article.timestamp),
          href: article.url,
        });
      });

    const cycle = mobileSectorRotationData?.marketCycle;
    if (cycle?.currentPhase) {
      cycleItems.push({
        type: 'cycle',
        id: 'cycle-phase',
        label: 'Market Cycle',
        title: formatPhaseTitle(cycle.currentPhase),
        fullTitle: formatPhaseTitle(cycle.currentPhase),
        subtitle: truncateText(cycle.description, 110),
        fullSubtitle: cycle.description,
        meta: cycle.confidence ? `${cycle.confidence} Confidence` : undefined,
      });
      if (cycle.transition?.transitioning) {
        cycleItems.push({
          type: 'cycle',
          id: 'cycle-transition',
          label: 'Cycle Transition',
          title: truncateText(cycle.transition.direction, 100),
          fullTitle: cycle.transition.direction,
          subtitle: `${cycle.transition.confidence} confidence`,
          fullSubtitle: `${cycle.transition.confidence} confidence`,
        });
      }
    }

    const sectorHighlights = mobileSectorRotationData?.interpretation?.sector_highlights;
    if (sectorHighlights?.leadership) {
      sectorItems.push({
        type: 'sector',
        id: 'sector-leadership',
        label: 'Leadership',
        title: truncateText(sectorHighlights.leadership, 110),
        fullTitle: sectorHighlights.leadership,
      });
    }
    if (sectorHighlights?.weakness) {
      riskItems.push({
        type: 'risk',
        id: 'sector-weakness',
        label: 'Weakness',
        title: truncateText(sectorHighlights.weakness, 110),
        fullTitle: sectorHighlights.weakness,
      });
    }
    if (sectorHighlights?.notable_changes) {
      sectorItems.push({
        type: 'sector',
        id: 'sector-changes',
        label: 'Notable Changes',
        title: truncateText(sectorHighlights.notable_changes, 110),
        fullTitle: sectorHighlights.notable_changes,
      });
    }

    const keyObservations = mobileSectorRotationData?.interpretation?.key_observations;
    if (Array.isArray(keyObservations)) {
      keyObservations.slice(0, 3).forEach((observation, index) => {
        const observationTitle = observation.observation || '';
        const observationImplication = observation.implication || '';
        riskItems.push({
          type: 'risk',
          id: `observation-${index}`,
          label: `Key Observation ${index + 1}`,
          title: truncateText(observationTitle, 110),
          fullTitle: observationTitle,
          subtitle: observationImplication ? truncateText(observationImplication, 110) : undefined,
          fullSubtitle: observationImplication || undefined,
        });
      });
    }

    const marketPhase = mobileSectorRotationData?.interpretation?.market_phase;
    if (marketPhase?.risks) {
      riskItems.push({
        type: 'risk',
        id: 'market-phase-risks',
        label: 'Market Risks',
        title: truncateText(marketPhase.risks, 120),
        fullTitle: marketPhase.risks,
      });
    }
    if (marketPhase?.opportunities) {
      sectorItems.push({
        type: 'sector',
        id: 'market-phase-opportunities',
        label: 'Opportunities',
        title: truncateText(marketPhase.opportunities, 120),
        fullTitle: marketPhase.opportunities,
      });
    }

    const sectorDetails = Array.isArray(mobileSectorRotationData?.sectorDetails)
      ? mobileSectorRotationData.sectorDetails
      : [];
    if (sectorDetails.length) {
      const groups = sectorDetails.reduce((acc, sector) => {
        const key = sector.momentum_state;
        if (!acc[key]) acc[key] = [];
        acc[key].push(sector);
        return acc;
      }, {});

      if (Array.isArray(groups.WEAK_UP) && groups.WEAK_UP.length) {
        const improving = groups.WEAK_UP.slice(0, 3)
          .map((sector) => `${sector.sector} (${formatPercent(sector.daily_momentum)})`)
          .join(', ');
        if (improving) {
          sectorItems.push({
            type: 'sector',
            id: 'sector-improving',
            label: 'Improving Sectors',
            title: improving,
            fullTitle: improving,
          });
        }
      }

      if (Array.isArray(groups.WEAK_DOWN) && groups.WEAK_DOWN.length) {
        const weakening = groups.WEAK_DOWN.slice(0, 3)
          .map((sector) => `${sector.sector} (${formatPercent(sector.daily_momentum)})`)
          .join(', ');
        if (weakening) {
          riskItems.push({
            type: 'risk',
            id: 'sector-weakening',
            label: 'Weakening Sectors',
            title: weakening,
            fullTitle: weakening,
          });
        }
      }
    }

    const scenarios = Array.isArray(mobileMacroScenariosData?.scenarios)
      ? mobileMacroScenariosData.scenarios
      : [];
    scenarios
      .filter((scenario) => scenario?.title && scenario?.probability > 20)
      .slice(0, 5)
      .forEach((scenario, index) => {
        const scenarioDescription = scenario.description || '';
        const winners = scenario.sectorImplications?.winners?.slice(0, 3).join(', ');
        const challenged = scenario.sectorImplications?.challenged?.slice(0, 3).join(', ');
        const subtitleParts = [];
        if (winners) subtitleParts.push(`Winners: ${winners}`);
        if (challenged) subtitleParts.push(`Challenges: ${challenged}`);
        const subtitle = subtitleParts.join(' · ');
        const scenarioRisk = scenario.risks?.[0] || '';
        const fullSubtitleParts = [];
        if (winners) fullSubtitleParts.push(`Winners: ${winners}`);
        if (challenged) fullSubtitleParts.push(`Challenges: ${challenged}`);
        if (scenarioRisk) fullSubtitleParts.push(`Risk: ${scenarioRisk}`);
        const fullSubtitle = fullSubtitleParts.join(' · ');

        macroItems.push({
          type: 'macro',
          id: `macro-scenario-${index}`,
          label: scenario.title,
          title: truncateText(scenarioDescription, 110),
          fullTitle: scenarioDescription,
          subtitle: subtitle ? truncateText(subtitle, 90) : undefined,
          fullSubtitle: fullSubtitle || undefined,
          meta: `${Math.round(scenario.probability)}% probability`,
        });
      });

    const overview = mobileMarketOverviewData?.overview;
    if (overview) {
      if (overview.opening_overview) {
        marketItems.push({
          type: 'market',
          id: 'overview-opening',
          label: 'Opening Overview',
          title: truncateText(overview.opening_overview, 120),
          fullTitle: overview.opening_overview,
        });
      }

      if (Array.isArray(overview.key_news_and_market_reactions)) {
        overview.key_news_and_market_reactions.slice(0, 5).forEach((item, index) => {
          const headline = item.headline || '';
          const impact = item.impact || undefined;
          marketItems.push({
            type: 'market',
            id: `overview-news-${index}`,
            label: 'Market Reaction',
            title: truncateText(headline, 110),
            fullTitle: headline,
            subtitle: impact ? truncateText(impact, 100) : undefined,
            fullSubtitle: impact,
          });
        });
      }

      if (overview.global_considerations) {
        overview.global_considerations.slice(0, 5).forEach((item, index) => {
          macroItems.push({
            type: 'macro',
            id: `overview-global-${index}`,
            label: item.region || 'Global Watch',
            title: truncateText(item.insights || '', 110),
            fullTitle: item.insights || '',
          });
        });
      }

      if (overview.investment_recommendations) {
        overview.investment_recommendations.slice(0, 3).forEach((rec, index) => {
          marketItems.push({
            type: 'market',
            id: `overview-recommendation-${index}`,
            label: rec.strategy || 'Strategy',
            title: truncateText(rec.details || '', 110),
            fullTitle: rec.details || '',
          });
        });
      }

      if (Array.isArray(overview.stock_moves_and_sector_highlights)) {
        overview.stock_moves_and_sector_highlights.forEach((sectorData, sectorIndex) => {
          const sectorName = sectorData.sector || 'Market';
          (sectorData.stocks || []).slice(0, 3).forEach((stock, stockIndex) => {
            const ticker = stock.ticker || '';
            // The generator is an LLM and has emitted 0 for a live stock (AMD, 2026-09-15, issue #332):
            // a zero price is absence written as a number, so only a positive one is shown.
            const priceStr = typeof stock.current_price === 'number' && stock.current_price > 0
              ? `$${stock.current_price.toFixed(2)}` : '';
            const movement = stock.movement_description || '';
            const stockTitle = ticker ? `${ticker}${priceStr ? ` @ ${priceStr}` : ''}` : stock.company;
            stockNewsItems.push({
              type: 'stockNews',
              id: `overview-stock-${sectorIndex}-${stockIndex}`,
              label: sectorName,
              title: stockTitle,
              fullTitle: `${stock.company} (${ticker})${priceStr ? ` @ ${priceStr}` : ''}`,
              subtitle: truncateText(movement, 100),
              fullSubtitle: movement,
            });
          });
        });
      }

      if (overview.closing_summary) {
        marketItems.push({
          type: 'market',
          id: 'overview-closing',
          label: 'Closing Summary',
          title: truncateText(overview.closing_summary, 120),
          fullTitle: overview.closing_summary,
        });
      }

      if (overview.upcoming_earnings_and_economic_events) {
        const upcoming = overview.upcoming_earnings_and_economic_events;
        if (Array.isArray(upcoming.upcoming_earnings)) {
          upcoming.upcoming_earnings.slice(0, 5).forEach((item, index) => {
            if (!isDateInRange(item.report_date, { minOffsetDays: 0, maxOffsetDays: 7 })) {
              return;
            }
            const eventTitle = `${item.company} • ${item.report_date}`;
            const eventImpact = item.expected_impact || '';
            eventItems.push({
              type: 'event',
              id: `overview-earnings-${index}`,
              label: 'Upcoming Earnings',
              title: eventTitle,
              fullTitle: eventTitle,
              subtitle: truncateText(eventImpact, 100),
              fullSubtitle: eventImpact,
            });
          });
        }
        if (Array.isArray(upcoming.upcoming_economic_events)) {
          upcoming.upcoming_economic_events.slice(0, 5).forEach((item, index) => {
            if (!isDateInRange(item.date, { minOffsetDays: 0, maxOffsetDays: 7 })) {
              return;
            }
            const eventTitle = `${item.event} • ${item.date}`;
            const eventDetails = item.details || '';
            eventItems.push({
              type: 'event',
              id: `overview-economic-${index}`,
              label: 'Upcoming Event',
              title: eventTitle,
              fullTitle: eventTitle,
              subtitle: truncateText(eventDetails, 100),
              fullSubtitle: eventDetails,
            });
          });
        }
      }
    }

    if (mobileBondHighlights) {
      if (mobileBondHighlights.slopeSummary) {
        bondItems.push({
          type: 'bond',
          id: 'bond-slope',
          label: 'Yield Curve',
          title: truncateText(mobileBondHighlights.slopeSummary, 120),
          fullTitle: mobileBondHighlights.slopeSummary,
          subtitle: mobileBondHighlights.slopeMeaning
            ? truncateText(mobileBondHighlights.slopeMeaning, 110)
            : undefined,
          fullSubtitle: mobileBondHighlights.slopeMeaning || undefined,
          meta:
            typeof mobileBondHighlights.slope === 'number'
              ? `10Y minus 3M: ${formatSignedNumber(mobileBondHighlights.slope)} pts`
              : undefined,
        });
      }
      if (mobileBondHighlights.correlationSummary) {
        bondItems.push({
          type: 'bond',
          id: 'bond-correlation',
          label: 'Yield vs Zero',
          title: truncateText(mobileBondHighlights.correlationSummary, 120),
          fullTitle: mobileBondHighlights.correlationSummary,
          meta:
            typeof mobileBondHighlights.correlation === 'number'
              ? `ρ ${formatSignedNumber(mobileBondHighlights.correlation, 2)}`
              : undefined,
        });
      }
      if (mobileBondHighlights.interpretation) {
        bondItems.push({
          type: 'bond',
          id: 'bond-interpretation',
          label: 'Desk Take',
          title: truncateText(mobileBondHighlights.interpretation, 120),
          fullTitle: mobileBondHighlights.interpretation,
        });
      }
    }

    (news || [])
      .filter((article) => article && article.title)
      .slice(0, 10)
      .forEach((article, index) => {
        const articleTitle = article.title;
        const articleSource = article.source || 'Market';
        newsItems.push({
          type: 'news',
          id: `news-${article.url || index}`,
          title: truncateText(articleTitle, 100),
          fullTitle: articleTitle,
          subtitle: articleSource,
          fullSubtitle: articleSource,
          meta: formatRelativeTimeFromNow(article.publishedAt),
          href: article.url,
        });
      });

    // Get upcoming economic events directly from allEconomicEvents (bypass user timeframe filter)
    const upcomingEconEvents = [];
    if (allEconomicEvents?.events) {
      Object.entries(allEconomicEvents.events).forEach(([date, events]) => {
        if (isDateInRange(date, { minOffsetDays: 0, maxOffsetDays: 7 })) {
          events.forEach((event) => {
            if (event?.name) {
              upcomingEconEvents.push({ ...event, date });
            }
          });
        }
      });
    }
    upcomingEconEvents.slice(0, 10).forEach((event, index) => {
      const eventName = event.name;
      const eventDateLabel = formatEventDateLabel(event.date);
      eventItems.push({
        type: 'event',
        id: `event-${event.name}-${event.date}-${index}`,
        title: truncateText(eventName, 80),
        fullTitle: eventName,
        subtitle: eventDateLabel,
        fullSubtitle: eventDateLabel,
        meta: getEventMeta(event),
      });
    });

    if (Array.isArray(news) && news.length && Array.isArray(companiesData) && companiesData.length) {
      const tickerToArticle = new Map();

      news.forEach((article) => {
        (article?.tickers || []).forEach((ticker) => {
          const normalizedTicker = cleanTicker(ticker);
          if (normalizedTicker && !tickerToArticle.has(normalizedTicker)) {
            tickerToArticle.set(normalizedTicker, article);
          }
        });
      });

      for (const company of companiesData) {
        const article = tickerToArticle.get(company.tickerSymbol);
        if (article) {
          stockNewsItems.push({
            type: 'stockNews',
            id: `stock-news-${company.tickerSymbol}`,
            title: `${company.tickerSymbol} · ${company.name || 'In Focus'}`,
            fullTitle: `${company.tickerSymbol} · ${company.name || 'In Focus'}`,
            subtitle: truncateText(article.title, 90),
            fullSubtitle: article.title,
            meta: article.source || 'News',
            href: article.url,
          });
        }

        if (stockNewsItems.length >= 6) {
          break;
        }
      }
    }

    // Portfolio stocks with upcoming earnings (within 7 days)
    if (Array.isArray(companiesData) && companiesData.length) {
      const upcomingEarnings = companiesData
        .map((company) => {
          const days = daysUntilEarnings(company.earningsDate);
          if (!reportsWithinHorizon(days)) {
            return null;
          }
          return { company, days };
        })
        .filter(Boolean)
        .sort((a, b) => a.days - b.days);

      upcomingEarnings.slice(0, 5).forEach(({ company, days }) => {
        let meta;
        if (days === 0) {
          meta = 'Reports today';
        } else if (days === 1) {
          meta = 'Reports tomorrow';
        } else {
          meta = `Reports in ${days} days`;
        }

        portfolioEarningsItems.push({
          type: 'portfolioEarnings',
          id: `portfolio-earnings-${company.tickerSymbol}`,
          label: 'Your Earnings',
          title: `${company.tickerSymbol} · ${company.name || 'Earnings Soon'}`,
          fullTitle: `${company.tickerSymbol} · ${company.name || 'Earnings Soon'}`,
          meta,
        });
      });
    }

    const strongSignalCandidates = Array.isArray(companiesData)
      ? companiesData
          .map((company) => {
            const topSignal = getTopSignal(company?.technicalSignals);
            if (!topSignal || topSignal.strength < 0.6) {
              return null;
            }
            return { company, signal: topSignal };
          })
          .filter(Boolean)
      : [];

    const sortedSignals = strongSignalCandidates.sort(
      (a, b) => (b.signal.strength || 0) - (a.signal.strength || 0)
    );

    sortedSignals
      .filter(({ signal }) => signal.type?.toUpperCase() === 'BUY')
      .slice(0, 7)
      .forEach(({ company, signal }) => {
        buySignalItems.push({
          type: 'signal',
          id: `buy-signal-${company.tickerSymbol}-${signal.name}`,
          label: 'Buy Signal',
          title: `${company.tickerSymbol} · ${company.name || 'Opportunity'}`,
          fullTitle: `${company.tickerSymbol} · ${company.name || 'Opportunity'}`,
          subtitle: truncateText(signal.name, 80),
          fullSubtitle: signal.name,
          meta: `Strength ${Math.round((signal.strength || 0) * 100)}%`,
        });
      });

    sortedSignals
      .filter(({ signal }) => signal.type?.toUpperCase() === 'SELL')
      .slice(0, 7)
      .forEach(({ company, signal }) => {
        sellSignalItems.push({
          type: 'sellSignal',
          id: `sell-signal-${company.tickerSymbol}-${signal.name}`,
          label: 'Sell Signal',
          title: `${company.tickerSymbol} · ${company.name || 'Risk Alert'}`,
          fullTitle: `${company.tickerSymbol} · ${company.name || 'Risk Alert'}`,
          subtitle: truncateText(signal.name, 80),
          fullSubtitle: signal.name,
          meta: `Strength ${Math.round((signal.strength || 0) * 100)}%`,
        });
      });

    // Movers: today's biggest gainers and losers in the list on screen.
    //
    // Ranked on getCompanyChangePercent, i.e. regularMarketChangePercent ONLY — the
    // whole question is "what moved today", and the neighbouring fields answer other
    // ones (stockChange is the drift since the last report, fiftyTwoWeekChangePercent
    // a year of it). A stock with no live quote drops out of the ranking rather than
    // entering it on the wrong number.
    if (Array.isArray(companiesData) && companiesData.length) {
      const rankedByMove = rankByMove(companiesData, todaysMoveOnly(companiesData, getCompanyChangePercent));

      const moverLabel = activeListName || 'Your List';
      const priceOf = (company) =>
        typeof company?.stockPrice === 'number' && Number.isFinite(company.stockPrice)
          ? `$${company.stockPrice.toFixed(2)}`
          : '';

      const pushMover = (entry, rank, direction) => {
        const { company, change } = entry;
        const price = priceOf(company);
        const name = company.name || company.tickerSymbol;
        // The explanation (issue #317) replaces the name as the card text when the worker has
        // one; a theme member gets its theme's prose, prefixed with the theme title.
        const why = explanationFor(mobileMoverNews, company.tickerSymbol);
        const text = why ? (why.theme ? `${why.theme.title}: ${why.text}` : why.text) : name;
        return {
          type: direction === 'up' ? 'mover' : 'moverDown',
          id: `mover-${direction}-${company.tickerSymbol}`,
          label: `${moverLabel} · ${direction === 'up' ? 'Gainer' : 'Decliner'} #${rank}`,
          title: `${company.tickerSymbol}${price ? ` · ${price}` : ''}`,
          fullTitle: `${company.tickerSymbol} · ${name}${price ? ` · ${price}` : ''}`,
          subtitle: truncateText(text, 110),
          fullSubtitle: text,
          meta: formatPercent(change, { fractionDigits: 2 }),
          href: why?.sources?.[0]?.url,
          visual: rangeBarVisual(company),
        };
      };

      // A shared driver appears ONCE as its own card, ahead of the per-stock movers it explains.
      (mobileMoverNews?.themes || []).forEach((theme) => {
        const members = theme.tickers
          .map((t) => `${t} ${fmtMovePct(mobileMoverNews.movers?.[t]?.changePercent)}`.trim())
          .join(' · ');
        moverItems.push({
          type: 'moverTheme',
          id: `mover-theme-${theme.id}`,
          label: 'Your watchlist · Shared driver',
          title: theme.title,
          fullTitle: theme.title,
          subtitle: truncateText(theme.summary, 110),
          fullSubtitle: theme.summary,
          meta: members,
          href: theme.sources?.[0]?.url,
        });
      });

      const { gainers, decliners } = topMovers(rankedByMove, MOVERS_PER_SIDE);

      gainers.forEach((entry, index) => moverItems.push(pushMover(entry, index + 1, 'up')));
      decliners.forEach((entry, index) => moverItems.push(pushMover(entry, index + 1, 'down')));
    }

    // Smart lists on the move (issue #341): today's leading and lagging smart lists, one card each,
    // ranked by the SAME function the desktop Lists card renders. `listId` gives the card its
    // open-list control; the tape calls back with it rather than the memo closing over
    // handleCategorySelect, which is declared further down the component.
    const listMovers = rankListMovers(listPerformance, categories);
    const pushListMover = (entry, rank, direction) => listMoverItems.push({
      type: direction === 'up' ? 'listUp' : 'listDown',
      id: `list-mover-${direction}-${entry.id}`,
      label: `Smart lists on the move · ${direction === 'up' ? 'Leading' : 'Lagging'} #${rank}`,
      title: entry.name,
      fullTitle: entry.name,
      subtitle: `${breadthText(entry)} of ${entry.stocksWithData} stocks`,
      meta: formatListMove(entry.avgChange),
      listId: entry.id,
    });
    listMovers.leaders.forEach((entry, index) => pushListMover(entry, index + 1, 'up'));
    listMovers.laggards.forEach((entry, index) => pushListMover(entry, index + 1, 'down'));

    // Earnings-call takeaways for stocks that just reported.
    //
    // `headline` is the extraction's own <=20-word line, so nothing is re-summarised
    // here. Non-power users come back as `{locked:true}` sentinels: those collapse to
    // ONE teaser naming a few tickers rather than a locked card per stock, which would
    // fill the feed with things the reader cannot open.
    if (mobileCallHighlights && typeof mobileCallHighlights === 'object') {
      const companyByTicker = new Map(
        (Array.isArray(companiesData) ? companiesData : []).map((company) => [
          company.tickerSymbol,
          company,
        ])
      );
      const lockedTickers = [];

      Object.entries(mobileCallHighlights).forEach(([ticker, entry]) => {
        if (!entry) return;
        if (entry.locked) {
          lockedTickers.push(ticker);
          return;
        }
        const headline = typeof entry.headline === 'string' ? entry.headline.trim() : '';
        if (!headline) return;

        // Tone and guidance are the extraction's own short tags. Reaction prefers the
        // settled post-earnings move already mapped by api.js over the after-hours
        // print stored at extraction time — same quarter either way, since both come
        // off one earnings_history row.
        const company = companyByTicker.get(ticker);
        const metaParts = [];
        const direction = entry.guidance?.direction;
        if (direction && direction !== 'none') {
          metaParts.push(`Guidance ${direction}`);
        }
        if (entry.tone?.label) {
          metaParts.push(entry.tone.label);
        }
        const reaction =
          typeof company?.earningsReactionPercent === 'number'
            ? company.earningsReactionPercent
            : typeof entry.afterHours?.percent === 'number'
              ? entry.afterHours.percent
              : null;
        if (reaction !== null) {
          metaParts.push(`Reacted ${formatPercent(reaction, { fractionDigits: 1 })}`);
        }

        const why = typeof entry.tone?.why === 'string' ? entry.tone.why.trim() : '';
        const detail = typeof entry.guidance?.detail === 'string' ? entry.guidance.detail.trim() : '';
        const supporting = why || detail;

        callHighlightItems.push({
          type: 'callHighlight',
          id: `call-highlight-${ticker}`,
          label: `${ticker} earnings call`,
          title: truncateText(headline, 110),
          fullTitle: headline,
          subtitle: supporting ? truncateText(supporting, 100) : undefined,
          fullSubtitle: supporting || undefined,
          meta: metaParts.length ? metaParts.join(' · ') : undefined,
        });
      });

      if (!callHighlightItems.length && lockedTickers.length) {
        const shown = lockedTickers.slice(0, 3).join(', ');
        const more = lockedTickers.length - Math.min(3, lockedTickers.length);
        const lockedTitle = `Call takeaways ready for ${shown}${more > 0 ? ` +${more} more` : ''}`;
        callHighlightItems.push({
          type: 'callHighlight',
          id: 'call-highlight-locked',
          label: 'Earnings Call Takeaways',
          title: lockedTitle,
          fullTitle: lockedTitle,
          subtitle: 'AI summaries of the calls your stocks just held — a Pro feature.',
          fullSubtitle: 'AI summaries of the calls your stocks just held — a Pro feature.',
        });
      }
    }

    // Keyed by section so the picker and the feed cannot drift: MOBILE_FEED_SECTIONS
    // drives the order AND the toggles, and a section with no bucket here would show
    // up immediately as a toggle that turns nothing off.
    const groupsByKey = {
      summary: summaryItems,
      sentiment: sentimentItems,
      breadth: breadthItems,
      cycle: cycleItems,
      sector: sectorItems,
      risk: riskItems,
      macro: macroItems,
      market: marketItems,
      bond: bondItems,
      news: newsItems,
      event: eventItems,
      stockNews: stockNewsItems,
      movers: moverItems,
      buckets: buildCrowdedBucketCards(assetBuckets),
      listMovers: listMoverItems,
      callHighlight: callHighlightItems,
      guidanceSeason: buildGuidanceSeasonCard(guidanceSeasons),
      portfolioEarnings: portfolioEarningsItems,
      buySignal: buySignalItems,
      sellSignal: sellSignalItems,
    };

    const groups = MOBILE_FEED_SECTIONS.filter((section) =>
      isFeedSectionEnabled(mobileFeedSections, section.key)
    ).map((section) => groupsByKey[section.key] || []);

    const allItems = [];
    let index = 0;

    while (true) {
      let inserted = false;
      for (const group of groups) {
        if (group[index]) {
          allItems.push(group[index]);
          inserted = true;
        }
      }
      if (!inserted) {
        break;
      }
      index += 1;
    }

    const maxItems = mobileBatchIndex * MOBILE_BATCH_SIZE;
    const visibleItems = allItems.slice(0, maxItems);
    const hasMore = visibleItems.length < allItems.length;

    return { items: visibleItems, hasMore };
  }, [
    isMobile,
    listExplanation,
    selectedTab,
    userListsData,
    categories,
    allEconomicEvents,
    marketAnalysisData,
    mobileMarketStats,
    mobileFearGreedData,
    mobileSectorRotationData,
    mobileMacroScenariosData,
    mobileMarketOverviewData,
    mobileBondHighlights,
    mobileCallHighlights,
    mobileMoverNews,
    listPerformance,
    assetBuckets,
    guidanceSeasons,
    mobileFeedSections,
    labelsData,
    news,
    companiesData,
    mobileBatchIndex,
    strategies,
  ]);

  // Add state for notes
  const [stockNotes, setStockNotes] = useState(() => {
    const savedNotes = localStorage.getItem('stockNotes');
    return savedNotes ? JSON.parse(savedNotes) : {};
  });

  // Function to handle notes changes
  const handleNoteChange = useCallback((tickerSymbol, noteText) => {
    setStockNotes(prevNotes => {
      const updatedNotes = {
        ...prevNotes,
        [tickerSymbol]: noteText
      };
      
      // Save to localStorage
      localStorage.setItem('stockNotes', JSON.stringify(updatedNotes));
      
      // If user is logged in, sync to server through userData
      if (loggedInUser) {
        updateUserData(loggedInUser, {
          stockNotes: updatedNotes
        }).catch(error => {
          console.error('Error saving notes to server:', error);
        });
      }
      
      return updatedNotes;
    });
  }, [loggedInUser, updateUserData]);

  // Inject notes into company data
  const companiesDataWithNotes = useMemo(() => {
    if (!companiesData || !stockNotes) return companiesData;

    let data = companiesData.map(company => ({
      ...company,
      notes: stockNotes[company.tickerSymbol] || ''
    }));

    // Filter by label if a label is selected (selectedTab starts with 'label_')
    if (selectedTab?.startsWith('label_') && labelsData?.assignments) {
      const labelId = selectedTab.replace('label_', '');
      data = data.filter(company => {
        const assignment = labelsData.assignments[company.tickerSymbol];
        return assignment?.labels?.includes(labelId);
      });
    }

    return data;
  }, [companiesData, stockNotes, selectedTab, labelsData]);

  // Sync notes when user logs in
  useEffect(() => {
    if (loggedInUser && !isSyncing.current) {
      const syncUserNotes = async () => {
        try {
          const result = await refetchWatchlistData();
          const serverNotes = result?.data?.stockNotes || {};
          
          // Merge server notes with local notes
          setStockNotes(prevNotes => {
            const mergedNotes = { ...prevNotes, ...serverNotes };
            localStorage.setItem('stockNotes', JSON.stringify(mergedNotes));
            return mergedNotes;
          });
        } catch (error) {
          console.error('Error syncing notes:', error);
        }
      };
      
      syncUserNotes();
    }
  }, [loggedInUser, refetchWatchlistData]);

  // Label management callbacks (must be defined before memoizedStockTable)
  const handleOpenLabelManager = useCallback(() => {
    setLabelManagerModal({ isOpen: true, mode: 'list' });
  }, []);

  const handleOpenPurchaseInfo = useCallback((symbol, label, currentPrice) => {
    setPurchaseInfoModal({ isOpen: true, symbol, label, currentPrice });
  }, []);

  const handleMcpClick = useCallback(() => {
    setModalContent({
      title: 'QuantApe MCP Server',
      content: <McpConnectPanel variant="full" token={localStorage.getItem('authToken') || undefined} />,
      size: 'lg',
    });
    setModalIsOpen(true);
  }, []);

  // Now define memoizedStockTable
  const memoizedStockTable = useMemo(
    () => (
      <StockTable
        companyData={companiesDataWithNotes}
        economicEvents={filteredEconomicEvents}
        econAnalysis={allEconomicEvents?.analysis}
        onAddToWatchList={handleAddToWatchList}
        onDelete={handleDeleteFromWatchList}
        onNotInterested={handleNotInterested}
        guidance={guidance}
        relatedEquities={relatedEquities}
        loadingTable={isLoading}
        selectedTimeframes={selectedTimeframes}
        setSelectedTimeframes={setSelectedTimeframes}
        geography={geography}
        setGeography={setGeography}
        showEconomicEvents={showEconomicEvents}
        setShowEconomicEvents={setShowEconomicEvents}
        selectedTab={selectedTab}
        colorSettings={colorSettings}
        columns={columns}
        onColumnReorder={handleColumnReorder}
        onColorSettingsClick={handleColorSettingsClick}
        userSettings={userSettings}
        onSettingsChange={handleSettingsChange}
        onColorSettingsSave={handleColorSettingsSave}
        listExplanation={listExplanation}
        news={news}
        onNoteChange={handleNoteChange}
        loggedInUser={loggedInUser}
        existingWatchlistSymbols={companiesSymbols}
        labelsData={labelsData}
        onOpenLabelManager={handleOpenLabelManager}
        onOpenPurchaseInfo={handleOpenPurchaseInfo}
        initialExpandSymbol={initialExpandSymbol}
        focusSymbol={focusSymbol}
        listPath={listPath}
        onBatchAddComplete={async () => {
          // Clear batch flag and sync to server
          isBatchAddingRef.current = false;
          if (loggedInUser?.email) {
            try {
              const currentSymbols = JSON.parse(localStorage.getItem('companiesSymbols') || '[]');
              await updateWatchlist(loggedInUser, currentSymbols);
            } catch (error) {
              console.error('Batch sync failed:', error);
            }
          }
        }}
      />
    ),
    [companiesDataWithNotes, filteredEconomicEvents, handleAddToWatchList, handleDeleteFromWatchList,
     handleNotInterested, guidance, relatedEquities, isLoading, selectedTimeframes,
     setSelectedTimeframes, geography, setGeography, showEconomicEvents,
     setShowEconomicEvents, selectedTab, colorSettings, columns, handleColumnReorder,
     handleColorSettingsClick, userSettings, handleSettingsChange, handleColorSettingsSave,
     userListsData, selectedTab, listExplanation, news, handleNoteChange, loggedInUser, companiesSymbols, updateWatchlist,
     labelsData, handleOpenLabelManager, handleOpenPurchaseInfo, focusSymbol, listPath]
  );

  if (DEBUG.settings) {
    console.log('APP_STATUS in App.js:', APP_STATUS);
  }

  const handleDeleteProfile = useCallback(async () => {
    if (window.confirm("Are you sure you want to delete your profile? This action cannot be undone.")) {
      try {
        // Implement the delete profile logic here
        // For example:
        // await deleteUserProfile(loggedInUser);
        setLoggedInUser(null);
        localStorage.removeItem('authToken');
        setModalIsOpen(false);
        setStatusMessage('Your profile has been deleted successfully.');
        setStatusType('success');
      } catch (error) {
        console.error('Error deleting profile:', error);
        setStatusMessage('Failed to delete profile. Please try again.');
        setStatusType('error');
      }
    }
  }, [loggedInUser, setStatusMessage, setStatusType]);

  const handleProfileClick = useCallback(() => {
    setModalContent({
      title: 'Profile',
      content: <ProfileModal loggedInUser={loggedInUser} onDeleteProfile={handleDeleteProfile} onClose={() => setModalIsOpen(false)} />,
      size: 'lg',
      flush: true
    });
    setModalIsOpen(true);
  }, [loggedInUser, handleDeleteProfile, userListsData]);

  const handleNotificationsClick = useCallback(() => {
    const email = typeof loggedInUser === 'string' ? loggedInUser : loggedInUser?.email;
    setModalContent({
      title: 'Notifications',
      content: <NotificationsModal userEmail={email} userLists={userListsData} />,
      size: 'lg',
    });
    setModalIsOpen(true);
  }, [loggedInUser, userListsData]);

  const handleSubscriptionClick = useCallback(() => {
    setModalContent({
      title: 'Subscription',
      content: <p>Subscription management options will go here.</p>
    });
    setModalIsOpen(true);
  }, []);

  useEffect(() => {
    console.log('Modal open state changed:', modalIsOpen);
  }, [modalIsOpen]);

  const handleAddSymbolClick = useCallback(() => {
    setModalContent({
      title: 'Add Symbol to Watch List',
      content: (
        <AddSymbolButton 
          onAddSymbol={handleAddToWatchList}
          existingSymbols={companiesSymbols}
          isLoggedIn={!!loggedInUser}
          className="w-full"
        >
          Search and add symbols to your watch list
        </AddSymbolButton>
      )
    });
    setModalIsOpen(true);
  }, [handleAddToWatchList, companiesSymbols, loggedInUser]);


  useEffect(() => {
    if (userSettings.colorSettings) {
      setColorSettings(userSettings.colorSettings);
    }
    
    if (userSettings.columns) {
      const savedColumnKeys = userSettings.columns;
      const fixedColumns = ALL_COLUMNS.filter(col => col.isFixed);
      const fixedKeys = fixedColumns.map(col => col.key);
      
      // Combine fixed and saved columns, maintaining order
      const allColumnKeys = [...fixedKeys, ...savedColumnKeys.filter(key => !fixedKeys.includes(key))];
      
      const allColumns = allColumnKeys
        .map(key => ALL_COLUMNS.find(col => col.key === key))
        .filter(Boolean);
      
      if (allColumns.length > 0) {
        setColumns(allColumns);
      }
    }
  }, [userSettings]);

  if (DEBUG.columns) {
    console.log('columns in App.js before rendering StockTable:', columns);
  }
  // Load user preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const savedSettings = await loadSettings();
        setUserSettings(savedSettings);

        if (savedSettings.columns) {
          const savedColumnKeys = savedSettings.columns;
          const fixedColumns = ALL_COLUMNS.filter((col) => col.isFixed).map(
            (col) => col.key
          );

          const allColumnKeys = Array.from(
            new Set([...fixedColumns, ...savedColumnKeys])
          );

          const allColumns = allColumnKeys
            .map((key) => ALL_COLUMNS.find((col) => col.key === key))
            .filter(Boolean);

          if (allColumns.length > 0) {
            setColumns(allColumns);
          }
        }
        // ... handle other settings ...
      } catch (error) {
        console.error("Error loading user preferences:", error);
      }
    };

    loadUserPreferences();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.inline-flex')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const handleDropdownToggle = (categoryId) => {
    setActiveDropdown(current => current === categoryId ? null : categoryId);
  };

  // Jump to a stock's row in the table (clicked from the Signal Watches widget).
  // If it isn't in the currently loaded list, switch to a list that has it —
  // the watchlist when it's there, otherwise a single-symbol virtual tab — and
  // let StockTable resolve the pending focus once that data lands.
  const handleFocusSymbol = useCallback((symbol) => {
    const sym = String(symbol || '').trim().toUpperCase();
    if (!sym) return;
    focusNonceRef.current += 1;
    setFocusSymbol({ symbol: sym, nonce: focusNonceRef.current });

    const inTable = companiesDataWithNotes?.some(c => c.tickerSymbol?.toUpperCase() === sym);
    if (inTable) return;
    const inWatchlist = companiesSymbols?.some(s => String(s).toUpperCase() === sym);
    console.log('[SIGNAL-FOCUS] Not in current list, loading:', sym, inWatchlist ? '(watchlist)' : '(single symbol)');
    setSelectedTab(inWatchlist ? 'watchlist' : `symbols_${sym}`);
  }, [companiesDataWithNotes, companiesSymbols]);

  // Update handleCategorySelect to handle custom filters
  const handleCategorySelect = (categoryId) => {
    console.log('handleCategorySelect called with categoryId:', categoryId);
    
    // Check if this is a custom filter
    const isCustomFilter = userListsData?.some(list => list.id === categoryId && list.type === 'custom_filter');
    
    if (isCustomFilter) {
      console.log('Selected a custom filter with ID:', categoryId);
      // Just set the selectedTab, loadData will handle the rest
      setSelectedTab(categoryId);
    } else if (categoryId === 'personal') {
      setSelectedTab('watchlist'); // Default to watchlist when My Lists is selected
    } else {
      setSelectedTab(categoryId);
    }
  };

  // Replace the filter persistence effect with an effect that handles both initial load and updates
  useEffect(() => {
    const savedSettings = loadSettings();
    if (savedSettings.marketCapFilters && 
        JSON.stringify(savedSettings.marketCapFilters) !== JSON.stringify(activeFilters.marketCap)) {
      console.log('Updating filters from saved settings:', savedSettings.marketCapFilters);
      setActiveFilters(prev => ({
        ...prev,
        marketCap: savedSettings.marketCapFilters
      }));
    }
  }, [activeFilters.marketCap]); // Only run when marketCap filters change

  // Add effect to log settings changes
  useEffect(() => {
    console.log('Current userSettings:', userSettings);
  }, [userSettings]);

  // Effect to handle empty watchlist
  useEffect(() => {
    const checkWatchlist = () => {
      console.log('Checking watchlist...');
      const savedSymbols = localStorage.getItem('companiesSymbols');
      const watchlistSymbols = savedSymbols ? JSON.parse(savedSymbols) : [];
      
      if (!watchlistSymbols.length && selectedTab === 'watchlist') {
        // Switch to the homepage default list when watchlist is empty
        setSelectedTab(HOMEPAGE_DEFAULT_TAB); // Changed from bellwether_stocks
      }
    };

    checkWatchlist();
  }, []); // Run once on mount

  // Modify handleAIFilterSuccess to NOT create the list again
  const handleAIFilterSuccess = useCallback(
    async (savedList) => {
      try {
        console.log('Received savedList:', savedList);

        if (!loggedInUser?.email) {
          setError("Please log in to create and save AI-generated lists");
          setStatusType("error");
          return;
        }

        setStatusMessage("Loading list data...");
        setStatusType("info");
        setIsLoading(true);

        try {
          // First, refetch the user lists to ensure we have the latest data
          await refetchUserLists();
          
          // Then update the selected tab to show we're on this list
          setSelectedTab(savedList.id);

          // Parse symbols from the savedList
          const symbols = Array.isArray(savedList.symbols) 
            ? savedList.symbols 
            : JSON.parse(savedList.symbols || '[]');

          // Fetch the data for these symbols
          const data = await fetchScreenData(null, null, symbols, activeFilters);
          if (data && Array.isArray(data)) {
            setCompaniesData(data);
            setStatusMessage("AI Filter list loaded successfully");
            setStatusType("success");
          } else {
            throw new Error("Failed to fetch data for symbols");
          }

        } catch (error) {
          console.error("Error loading list data:", error);
          
          // Show placeholder data
          const symbols = Array.isArray(savedList.symbols) 
            ? savedList.symbols 
            : JSON.parse(savedList.symbols || '[]');

          const fallbackRows = symbols.map(sym => ({
            tickerSymbol: sym,
            name: "Loading...",
            stockPrice: null,
          }));
          
          setCompaniesData(fallbackRows);
          setStatusMessage("List created. Loading data...");
          setStatusType("warning");
        } finally {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("handleAIFilterSuccess error:", error);
        console.error("savedList that caused error:", savedList);
        setError("Error loading AI filter list data");
        setStatusType("error");
        setIsLoading(false);
      }
    },
    [loggedInUser, setCompaniesData, setSelectedTab, setError, setStatusType, refetchUserLists, fetchScreenData]
  );

  // Add delete handler
  const handleDeleteList = async (listId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this list?')) {
        return;
      }

      console.log("Deleting list with ID:", listId);

      setStatusMessage('Deleting list...');
      setStatusType('info');

      if (loggedInUser?.email) {
        try {
          // Fixed: Pass the listId directly as a string parameter
          const result = await deleteListMutation.mutateAsync(listId);
          console.log("Delete success, result:", result);

          // Force a fresh read of userLists from the server to update the UI
          await refetchUserLists();

        } catch (error) {
          console.error('Failed to delete list from server:', error);
        }
      }

      if (selectedTab === listId) {
        setSelectedTab('watchlist');
      }

      setStatusMessage('List deleted successfully');
      setStatusType('success');

    } catch (error) {
      console.error('Error deleting list:', error);
      setError('Failed to delete list');
      setStatusType('error');
    }
  };

  // Sync any local-only lists during login
  useEffect(() => {
    const syncLocalLists = async () => {
      if (!loggedInUser?.email) {
        return;
      }

      try {
        const localSettings = loadSettings();
        const localLists = Array.isArray(localSettings?.lists) ? localSettings.lists : [];
        const unsyncedLists = localLists.filter((list) => !list?.id);

        if (!unsyncedLists.length) {
          return;
        }

        const createdLists = [];

        for (const list of unsyncedLists) {
          const symbols = Array.isArray(list?.symbols)
            ? list.symbols.filter(Boolean)
            : [];

          if (!symbols.length) {
            continue;
          }

          try {
            const payload = {
              ...list,
              owner_id: loggedInUser.email,
              symbols,
            };

            const saved = await createUserList(payload);
            if (saved?.id) {
              createdLists.push(saved);
            }
          } catch (syncError) {
            console.error('Failed to sync local list', list?.name, syncError);
          }
        }

        if (!createdLists.length) {
          return;
        }

        const updatedLists = [
          ...localLists.filter((list) => list?.id),
          ...createdLists,
        ];

        saveSettings('lists', updatedLists);
        setUserSettings((prev) => ({
          ...prev,
          lists: updatedLists,
        }));

        if (typeof refetchUserLists === 'function') {
          await refetchUserLists();
        }
      } catch (error) {
        console.error('Error syncing local lists:', error);
        setError('Failed to sync lists');
      }
    };

    syncLocalLists();
  }, [loggedInUser, refetchUserLists, createUserList]);

  useEffect(() => {
    // Only do this if we have a non-empty statusMessage
    if (statusMessage) {
      const timer = setTimeout(() => {
        // Clear out the message, error, and revert to 'info' after 5 seconds
        setStatusMessage('');
        setError('');
        setStatusType('info');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [statusMessage, setStatusMessage, setError, setStatusType]);

  const fetchLatestNews = useCallback(async () => {
    try {
      const response = await publicFetch(`${CONFIG.API_URLS.NEWS}`);
      const data = await response.json();
      if (data.articles) {
        setNews(data.articles);
        console.log('Fetched news articles:', data.articles.length);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  }, []);

  useEffect(() => {
    fetchLatestNews();
  }, [fetchLatestNews]);

  useEffect(() => {
    setMobileBatchIndex(1);
    setIsMobileLoadingMore(false);
  }, [selectedTab, isMobile]);

  useEffect(() => {
    if (isMobileLoadingMore) {
      setIsMobileLoadingMore(false);
    }
  }, [mobileBatchIndex, mobileHasMore]);



  const handleMobileLoadMore = useCallback(() => {
    if (isMobileLoadingMore || !mobileHasMore) {
      return;
    }
    setIsMobileLoadingMore(true);
    setMobileBatchIndex((prev) => prev + 1);
  }, [isMobileLoadingMore, mobileHasMore]);
  const handleMobileRefresh = useCallback(async () => {
    if (isMobileRefreshing) {
      return;
    }
    setIsMobileRefreshing(true);
    setIsMobileLoadingMore(false);
    setMobileBatchIndex(1);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['fearAndGreed'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['dailyMovementAnalytics'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['sectorRotation'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['macroScenarios'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['marketOverview'], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['curvesData'], type: 'active' }),
      ]);
      await fetchLatestNews();
      await loadData(true);
    } catch (error) {
      console.error('Error refreshing mobile data:', error);
    } finally {
      setIsMobileRefreshing(false);
    }
  }, [isMobileRefreshing, queryClient, fetchLatestNews, loadData]);

  // Removed redundant tab change effect - the main loadData effect already handles selectedTab changes
  // This was causing issues by clearing data when loadData function reference changed

  // Save type filter settings to localStorage when they change
  useEffect(() => {
    const savedSettings = loadSettings();
    if (
      userSettings?.typeFilters &&
      JSON.stringify(savedSettings.typeFilters) !== JSON.stringify(activeFilters.types)
    ) {
      saveSettings('typeFilters', activeFilters.types);
    }
  }, [activeFilters.types, userSettings]); // Only run when type filters change

  // Save market cap filter settings to localStorage when they change
  useEffect(() => {
    const savedSettings = loadSettings();
    if (
      userSettings?.marketCapFilters &&
      JSON.stringify(savedSettings.marketCapFilters) !== JSON.stringify(activeFilters.marketCap)
    ) {
      saveSettings('marketCapFilters', activeFilters.marketCap);
    }
  }, [activeFilters.marketCap, userSettings]); // Only run when marketCap filters change

  // Save sectors filter settings to localStorage when they change
  useEffect(() => {
    const savedSettings = loadSettings();
    if (
      userSettings?.sectorsFilters &&
      JSON.stringify(savedSettings.sectorsFilters) !== JSON.stringify(activeFilters.sectors)
    ) {
      saveSettings('sectorsFilters', activeFilters.sectors);
    }
  }, [activeFilters.sectors, userSettings]); // Only run when sectors filters change

  // Original onSelectTab function with custom filter handling
  const onSelectTab = async (tabId) => {
    console.log('onSelectTab called with tabId:', tabId);
    
    // We're now handling custom filters in the loadData function,
    // so we can just set the selectedTab here
    setSelectedTab(tabId);
  };

  // Debug: log render state
  console.log('[WATCHLIST-DEBUG] Render state:', { isLoading, companiesDataLength: companiesData?.length, loggedInUser: !!loggedInUser });

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginProvider onLoginSuccess={handleLoginSuccess} onLoginFailure={(error) => console.error('Login failed:', error)}>
        <Router>
          <Routes>
            {/* Anonymous visitors get the landing page on every route via
                AccessGate; this route just lets a signed-in owner preview it. */}
            <Route path="/landing" element={<LandingPage />} />
            {/* Self-hosted legal pages (issue #61) */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/mcp" element={<McpPage />} />
        {/* Main app - matches all paths, useEffect handles tab selection based on URL */}
        <Route
          path="/*"
          element={
            <QueryClientProvider client={queryClient}>
              <ListRouteSync
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                categories={categories}
                userListsData={userListsData}
              />
              <ListSEO
                selectedTab={selectedTab}
                categories={categories}
                userListsData={userListsData}
                strategies={strategies}
              />
              <div className="container mx-auto mt-4">
                <Header
                  onLogout={handleLogout}
                  loggedInUser={loggedInUser}
                  trialDaysLeft={trialDaysLeft}
                  isSubscribed={isSubscribed}
                  statusMessage={statusMessage}
                  statusType={statusType}
                  appStatus={APP_STATUS}
                  onProfileClick={handleProfileClick}
                  onNotificationsClick={handleNotificationsClick}
                  onMcpClick={handleMcpClick}
                  onSubscriptionClick={handleSubscriptionClick}
                  onSettingsSave={handleSettingsSave}
                  onPreferencesClick={() => setShowOnboardingWizard(true)}
                />
                {isMobile ? (
                  <>
                    {/* Mobile tab switcher */}
                    <div style={{
                      display: 'flex', background: '#f3f4f6', borderRadius: 10, padding: 3,
                      marginBottom: 12,
                    }}>
                      {[
                        { key: 'brief', label: 'Brief' },
                        { key: 'earnings', label: 'Earnings' },
                        { key: 'signals', label: 'Strategies' },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setMobileTab(tab.key)}
                          style={{
                            flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                            border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                            background: mobileTab === tab.key ? '#fff' : 'transparent',
                            color: mobileTab === tab.key ? '#111827' : '#6b7280',
                            boxShadow: mobileTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                          }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {mobileTab === 'earnings' ? (
                      <MobileEarningsView loggedInUser={loggedInUser} onLoginSuccess={handleLoginSuccess} onOpenLabelManager={handleOpenLabelManager} />
                    ) : mobileTab === 'signals' ? (
                      <StrategiesWidget loggedInUser={loggedInUser} mobile />
                    ) : (
                    <>
                    {/* Feed config lives in the Brief's own content, not the tab row.
                        It configures this tab only, and rendering it inside that flex
                        row made the three flex:1 tabs resize every time the user
                        switched away from Brief. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowMobileFeedSettings(true)}
                        aria-label="Customize your brief"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '6px 12px', borderRadius: 9999,
                          border: '1px solid #e2e8f0', background: '#fff',
                          color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                        }}
                      >
                        <FiSliders size={14} />
                        <span>Customize</span>
                        {hiddenFeedSectionCount > 0 && (
                          <span
                            style={{
                              background: '#eff6ff', color: '#2563eb', borderRadius: 9999,
                              padding: '1px 7px', fontSize: 11, fontWeight: 700,
                            }}
                          >
                            {hiddenFeedSectionCount} hidden
                          </span>
                        )}
                      </button>
                    </div>
                    {/* Quick glance notice - styled to match feed */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.85rem 1rem',
                        marginBottom: '1rem',
                        borderRadius: '1rem',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderLeft: '4px solid #f59e0b',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.12)',
                      }}
                    >
                      <span style={{ fontSize: '1.25rem' }}>📱</span>
                      <span style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 500 }}>
                        Quick glance mode — hop to desktop for full dashboard & tools
                      </span>
                    </div>
                    {/* AI Market Summary - styled to match feed cards but more prominent */}
                    {mobileMarketOverviewData?.overview?.closing_summary && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.75rem',
                          alignItems: 'flex-start',
                          padding: '1.1rem',
                          marginBottom: '1.5rem',
                          borderRadius: '1rem',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderLeft: '5px solid #2563eb',
                          boxShadow: '0 12px 28px rgba(37, 99, 235, 0.15)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '44px',
                            height: '44px',
                            borderRadius: '9999px',
                            background: 'rgba(37, 99, 235, 0.15)',
                            flexShrink: 0,
                            fontSize: '1.25rem',
                          }}
                        >
                          ✨
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              fontWeight: 700,
                              color: '#1d4ed8',
                            }}
                          >
                            AI Market Summary
                          </span>
                          <span
                            style={{
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              color: '#0f172a',
                              lineHeight: 1.5,
                            }}
                          >
                            {renderMarketText(mobileMarketOverviewData.overview.closing_summary)}
                          </span>
                          {(() => {
                            const isStale = mobileMarketOverviewData?.stale === true;
                            const updatedLabel = formatMarketOverviewUpdated(mobileMarketOverviewData?.generated_at);
                            // Absent generated_at (old reader deployed without the field) -> render nothing extra.
                            if (!updatedLabel) return null;
                            return (
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  fontWeight: isStale ? 600 : 500,
                                  color: isStale ? '#92400e' : '#64748b',
                                }}
                              >
                                {isStale ? `Out of date — last updated ${updatedLabel}` : `Updated ${updatedLabel}`}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {/* Market Pulse Stats - compact mobile version */}
                    {(mobileMarketStats?.data || mobileFearGreedData?.current) && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.75rem',
                          marginBottom: '1rem',
                        }}
                      >
                        {/* Breadth stat */}
                        {mobileMarketStats?.data && (() => {
                          const stats = mobileMarketStats.data;
                          const numUp = stats.numPriceIncreases ?? 0;
                          const numDown = stats.numPriceDecreases ?? 0;
                          const total = numUp + numDown;
                          if (total === 0) return null;
                          const advPct = Math.round((numUp / total) * 100);
                          const isBullish = advPct >= 50;
                          return (
                            <div
                              style={{
                                flex: '1 1 45%',
                                minWidth: '140px',
                                padding: '0.75rem',
                                borderRadius: '0.75rem',
                                background: '#fff',
                                border: '1px solid rgba(203, 213, 225, 0.6)',
                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                              }}
                            >
                              <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                                Market Breadth
                              </div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: isBullish ? '#16a34a' : '#dc2626' }}>
                                  {advPct}%
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {isBullish ? 'advancing' : 'declining'}
                                </span>
                              </div>
                              <div style={{
                                height: '4px',
                                borderRadius: '2px',
                                background: '#fee2e2',
                                marginTop: '0.5rem',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${advPct}%`,
                                  height: '100%',
                                  background: '#16a34a',
                                  borderRadius: '2px',
                                }} />
                              </div>
                            </div>
                          );
                        })()}
                        {/* Fear & Greed */}
                        {mobileFearGreedData?.current && (() => {
                          const { rating, score } = mobileFearGreedData.current;
                          const getColor = (r) => {
                            if (r === 'Extreme Fear') return '#dc2626';
                            if (r === 'Fear') return '#ea580c';
                            if (r === 'Greed') return '#16a34a';
                            if (r === 'Extreme Greed') return '#15803d';
                            return '#64748b';
                          };
                          return (
                            <div
                              style={{
                                flex: '1 1 45%',
                                minWidth: '140px',
                                padding: '0.75rem',
                                borderRadius: '0.75rem',
                                background: '#fff',
                                border: '1px solid rgba(203, 213, 225, 0.6)',
                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                              }}
                            >
                              <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                                Fear & Greed
                              </div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: getColor(rating) }}>
                                  {Math.round(score)}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: getColor(rating), fontWeight: 500 }}>
                                  {rating}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Leading Sector */}
                        {mobileMarketStats?.data?.topIndustriesIncreases?.[0] && (
                          <div
                            style={{
                              flex: '1 1 45%',
                              minWidth: '140px',
                              padding: '0.75rem',
                              borderRadius: '0.75rem',
                              background: '#fff',
                              border: '1px solid rgba(203, 213, 225, 0.6)',
                              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            }}
                          >
                            <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                              Leading Industry
                            </div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.3 }}>
                              {mobileMarketStats.data.topIndustriesIncreases[0].industry}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, marginTop: '0.15rem' }}>
                              {formatIndustryShare(mobileMarketStats.data.topIndustriesIncreases[0].percentage, 'up')}
                            </div>
                          </div>
                        )}
                        {/* Lagging Sector */}
                        {mobileMarketStats?.data?.topIndustriesDecreases?.[0] && (
                          <div
                            style={{
                              flex: '1 1 45%',
                              minWidth: '140px',
                              padding: '0.75rem',
                              borderRadius: '0.75rem',
                              background: '#fff',
                              border: '1px solid rgba(203, 213, 225, 0.6)',
                              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            }}
                          >
                            <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                              Lagging Industry
                            </div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.3 }}>
                              {mobileMarketStats.data.topIndustriesDecreases[0].industry}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: '0.15rem' }}>
                              {formatIndustryShare(mobileMarketStats.data.topIndustriesDecreases[0].percentage, 'down')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {mobileBadgeItems.length > 0 ? (
                      <MobileBadgeTape
                        items={mobileBadgeItems}
                        onRefresh={handleMobileRefresh}
                        isRefreshing={isMobileRefreshing}
                        onLoadMore={handleMobileLoadMore}
                        isLoadingMore={isMobileLoadingMore}
                        hasMore={mobileHasMore}
                        onOpenList={handleCategorySelect}
                      />
                    ) : hiddenFeedSectionCount === MOBILE_FEED_SECTIONS.length ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: '#64748b' }}>
                        <div style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                          Every section is hidden right now.
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowMobileFeedSettings(true)}
                          style={{
                            padding: '0.5rem 0.9rem', borderRadius: '0.6rem', border: '1px solid #cbd5e1',
                            background: '#fff', color: '#334155', fontSize: '0.8rem', fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Choose what to show
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: '#64748b' }}>
                        Insights will appear once data loads.
                      </div>
                    )}
                    {error && <div className="text-red-500 text-center p-4">{error}</div>}
                    <MobileFeedSettings
                      open={showMobileFeedSettings}
                      sections={mobileFeedSections}
                      onToggle={handleToggleFeedSection}
                      onSetAll={handleSetAllFeedSections}
                      onClose={() => setShowMobileFeedSettings(false)}
                    />
                    <Footer onMcpClick={handleMcpClick} />
                    </>
                    )}
                  </>
                ) : (
                  <>
                    {error && <div className="text-red-500 text-center p-4">{error}</div>}
                    <MarketAnalysisRow
                      analysis={analysis}
                      guidance={guidance}
                      relatedEquities={relatedEquities}
                      onAddSymbol={handleAddToWatchList}
                      userSettings={userSettings}
                      onSettingsChange={handleSettingsChange}
                      news={news}
                      loggedInUser={loggedInUser}
                      onSelectList={handleCategorySelect}
                    />
                    {/* ThesesPanel replaced by the earnings schedule to save vertical space — may bring back later
                    <ThesesPanel
                      onSelectList={handleCategorySelect}
                    />
                    */}
                    <StrategiesWidget loggedInUser={loggedInUser} onSymbolClick={handleFocusSymbol} />
                    <EarningsSchedulePanel
                      loggedInUser={loggedInUser}
                      onLoginSuccess={handleLoginSuccess}
                      onOpenLabelManager={handleOpenLabelManager}
                      onDaySelect={(date) => handleCategorySelect(date ? `earnings_day_${date}` : 'watchlist')}
                      selectedDay={selectedTab?.startsWith('earnings_day_') ? selectedTab.slice('earnings_day_'.length) : null}
                    />
                    <FilterDropdowns
                      selectedTab={selectedTab}
                      onSelectTab={handleCategorySelect}
                      onAddSymbol={handleAddToWatchList}
                      loggedInUser={loggedInUser}
                      existingSymbols={companiesSymbols}
                      activeFilters={activeFilters}
                      onFilterApply={handleFilterApply}
                      watchlist={companiesData}
                      categories={categories}
                      userListsData={userListsData}
                      onModalOpen={setModalIsOpen}
                      onModalContentSet={setModalContent}
                      onSuccess={handleAIFilterSuccess}
                      onDeleteList={handleDeleteList}
                      setError={setError}
                    />
                    {selectedTab?.startsWith('ptf_') ? (
                      <PortfolioView
                        portfolioId={selectedTab}
                        onBack={() => setSelectedTab('watchlist')}
                        loggedInUser={loggedInUser}
                      />
                    ) : (
                      memoizedStockTable
                    )}
                    <Footer onMcpClick={handleMcpClick} />
                  </>
                )}
                <SettingsDialog
                  isOpen={isColorSettingsOpen}
                  onClose={() => setIsColorSettingsOpen(false)}
                  onSave={handleColorSettingsSave}
                  initialSettings={colorSettings}
                />
                <SyncConflictDialog
                  isOpen={syncConflictData !== null}
                  onResolve={handleSyncConflictResolve}
                  localSymbols={syncConflictData?.localSymbols || []}
                  serverSymbols={syncConflictData?.serverSymbols || []}
                  localOnlySymbols={syncConflictData?.localOnlySymbols || []}
                  serverOnlySymbols={syncConflictData?.serverOnlySymbols || []}
                />
                {/* Label Manager Modal */}
                <Modal
                  isOpen={labelManagerModal.isOpen}
                  onRequestClose={() => setLabelManagerModal({ isOpen: false, mode: 'list' })}
                  contentLabel="Manage Labels"
                  className="modal-content"
                  overlayClassName="modal-overlay"
                >
                  <LabelManagerModal
                    userEmail={loggedInUser?.email}
                    onClose={() => setLabelManagerModal({ isOpen: false, mode: 'list' })}
                    initialMode={labelManagerModal.mode}
                  />
                </Modal>
                {/* Purchase Info Modal */}
                <Modal
                  isOpen={purchaseInfoModal.isOpen}
                  onRequestClose={() => setPurchaseInfoModal({ isOpen: false, symbol: null, label: null, currentPrice: null })}
                  contentLabel="Purchase Info"
                  className="modal-content"
                  overlayClassName="modal-overlay"
                >
                  <PurchaseInfoModal
                    symbol={purchaseInfoModal.symbol}
                    label={purchaseInfoModal.label}
                    userEmail={loggedInUser?.email}
                    onClose={() => setPurchaseInfoModal({ isOpen: false, symbol: null, label: null, currentPrice: null })}
                    currentPrice={purchaseInfoModal.currentPrice}
                  />
                </Modal>
                {isListsLoading && <div>Loading lists...</div>}
                {listsError && <div>Error loading lists: {listsError.message}</div>}
              </div>
            </QueryClientProvider>
          } 
        />
        <Route path="/earnings" element={
          <EarningsPage
            loggedInUser={loggedInUser}
            onLoginSuccess={handleLoginSuccess}
            onLogout={handleLogout}
            onOpenLabelManager={handleOpenLabelManager}
          />
        } />
        {/* Index of every list URL (/list/<slug>). Static path, so it outranks the /* app route. */}
        <Route path="/lists" element={
          <div className="container mx-auto mt-4">
            <Header
              onLogout={handleLogout}
              loggedInUser={loggedInUser}
              trialDaysLeft={trialDaysLeft}
              isSubscribed={isSubscribed}
              statusMessage={statusMessage}
              statusType={statusType}
              appStatus={APP_STATUS}
              onProfileClick={handleProfileClick}
              onNotificationsClick={handleNotificationsClick}
              onMcpClick={handleMcpClick}
              onSubscriptionClick={handleSubscriptionClick}
              onSettingsSave={handleSettingsSave}
              onPreferencesClick={() => setShowOnboardingWizard(true)}
            />
            <ListsIndexPage loggedInUser={loggedInUser} userListsData={userListsData} strategies={strategies} />
            <Footer onMcpClick={handleMcpClick} />
          </div>
        } />
        <Route path="/stock/:ticker" element={
          <>
            <Header
              onLogout={handleLogout}
              loggedInUser={loggedInUser}
              trialDaysLeft={trialDaysLeft}
              isSubscribed={isSubscribed}
              statusMessage={statusMessage}
              statusType={statusType}
              appStatus={APP_STATUS}
              onProfileClick={handleProfileClick}
              onNotificationsClick={handleNotificationsClick}
              onMcpClick={handleMcpClick}
              onSubscriptionClick={handleSubscriptionClick}
              onSettingsSave={handleSettingsSave}
              onPreferencesClick={() => setShowOnboardingWizard(true)}
            />
            <StockPage />
            <Footer onMcpClick={handleMcpClick} />
          </>
        } />
        {/* Note: /app, /screeners/*, /market-overview are handled by /* route above */}
      </Routes>

      {/* Modal + onboarding wizard hosts sit outside <Routes> so the Header/Footer buttons that
          open them (profile, notifications, MCP, subscription, preferences) work on every
          route, not just inside the /* app element. */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel={modalContent.title}
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <div className={`bg-white rounded-lg shadow-xl w-full mx-auto ${modalContent.size === 'lg' ? 'max-w-xl' : 'max-w-md'}`}>
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{modalContent.title}</h2>
            <button
              onClick={() => setModalIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className={modalContent.flush ? 'p-0' : 'p-4'} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            {modalContent.content}
          </div>
        </div>
      </Modal>
      <OnboardingWizard
        isOpen={showOnboardingWizard}
        onClose={() => setShowOnboardingWizard(false)}
        onComplete={handleOnboardingComplete}
        existingPreferences={userPreferences}
      />

      {/* Analytics consent banner (self-hosted, replaces Termly) */}
      <ConsentBanner />
      
          {/* Toast notification */}
          {toast.visible && (
            <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
              toast.type === 'error' ? 'bg-red-100 text-red-800' :
              toast.type === 'success' ? 'bg-green-100 text-green-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {toast.message}
            </div>
          )}
        </Router>
      </LoginProvider>
    </GoogleOAuthProvider>
  );
};

export default React.memo(App);
